import { normalizeMessengerV2ReadyInfo } from './groupReadiness.js';
import { resolveMessengerV2GroupInviteSummaryText } from './groupInvitePreview.js';
import {
  createInitialMessengerV2ViewState,
  reduceMessengerV2ViewState,
} from './messengerViewState.js';
import { createInitialMessengerV2CallState } from './messengerCallState.js';
import {
  applyMessengerV2TimelineReaction,
  markMessengerV2TimelineReactionState,
  settleMessengerV2TimelineReaction,
} from './messengerReactionState.js';
import { compareMessengerV2ReceiptFrontiers, toMessengerV2ServerSeq } from './receiptFrontier.js';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const toBooleanOrNull = (value) => {
  if (value === true || value === false) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return null;
};

const toNonNegativeIntegerOrNull = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized >= 0 ? normalized : null;
};

const normalizeConversationIdList = (value = []) => {
  const source = Array.isArray(value) ? value : [];
  return Array.from(
    new Set(source.map((item) => toStringOrNull(item)).filter((item) => item != null)),
  );
};

const isConversationHidden = (state = null, conversationId = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) return false;
  return normalizeConversationIdList(state?.hiddenConversationIds).includes(
    normalizedConversationId,
  );
};

const isGroupConversationRecord = (conversation = null) =>
  conversation?.kind !== 'room' &&
  (conversation?.kind === 'group' ||
  Boolean(
    toStringOrNull(
      conversation?.group_id ??
        conversation?.groupId ??
        conversation?.group?.id ??
        conversation?.group?.group_id ??
        conversation?.group?.groupId ??
        null,
    ),
  ));

const markConversationCatalogTrusted = (conversation = null, trusted = true) => {
  if (!conversation || typeof conversation !== 'object') {
    return conversation;
  }
  const nextTrusted = trusted === true;
  return {
    ...conversation,
    catalog_trusted: nextTrusted,
    catalogTrusted: nextTrusted,
  };
};

const markConversationsCatalogTrusted = (conversations = [], trusted = true) =>
  (Array.isArray(conversations) ? conversations : [])
    .map((conversation) => markConversationCatalogTrusted(conversation, trusted))
    .filter((conversation) => conversation && typeof conversation === 'object');

const isCatalogTrustedConversation = (conversation = null) =>
  conversation?.catalog_trusted === true || conversation?.catalogTrusted === true;

const filterHiddenConversations = (state = null, conversations = []) => {
  const hiddenIds = new Set(normalizeConversationIdList(state?.hiddenConversationIds));
  if (!hiddenIds.size) return Array.isArray(conversations) ? conversations : [];
  return (Array.isArray(conversations) ? conversations : []).filter(
    (conversation) => !hiddenIds.has(toStringOrNull(conversation?.id ?? null)),
  );
};

const addHiddenConversationId = (state = null, conversationId = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) return normalizeConversationIdList(state?.hiddenConversationIds);
  return normalizeConversationIdList([
    ...normalizeConversationIdList(state?.hiddenConversationIds),
    normalizedConversationId,
  ]);
};

const removeHiddenConversationIds = (state = null, conversationIds = []) => {
  const idsToRemove = new Set(normalizeConversationIdList(conversationIds));
  if (!idsToRemove.size) return normalizeConversationIdList(state?.hiddenConversationIds);
  return normalizeConversationIdList(state?.hiddenConversationIds).filter(
    (conversationId) => !idsToRemove.has(conversationId),
  );
};

const VOICE_CALL_SIGNAL_CONTENT_TYPE = 'application/vnd.nettgrav.messenger-v2.call-signal+mls';
const MLS_PENDING_RUNTIME_BODY = 'Encrypted MLS message pending runtime ready.';

const normalizeContentType = (value) => {
  const normalized = toStringOrNull(value);
  return normalized ? normalized.toLowerCase() : null;
};

const toFiniteServerSeq = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const toTimestamp = (value) => {
  const normalized = toStringOrNull(value);
  if (!normalized) return 0;
  const parsed = new Date(normalized).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveMembershipUpdatedAt = (conversation = null) =>
  toStringOrNull(
    conversation?.membership_updated_at ??
      conversation?.membershipUpdatedAt ??
      conversation?.group?.membership_updated_at ??
      conversation?.group?.membershipUpdatedAt ??
      null,
  );

const isAuthoritativeParticipantSnapshot = (conversation = null) =>
  Array.isArray(conversation?.participants) &&
  (conversation?.participants_authoritative === true ||
    conversation?.participantsAuthoritative === true);

const resolveScopedGroupMembershipStatus = (conversation = null) =>
  toStringOrNull(
    conversation?.current_user_group_membership_status ??
      conversation?.currentUserGroupMembershipStatus ??
      conversation?.group?.current_user_group_membership_status ??
      conversation?.group?.currentUserGroupMembershipStatus ??
      null,
  );

const conversationScopesCurrentUserAsActive = (conversation = null) =>
  resolveScopedGroupMembershipStatus(conversation) === 'active';

const resolveScopedConversationDeletedAt = (conversation = null) =>
  toStringOrNull(
    conversation?.current_user_conversation_deleted_at ??
      conversation?.currentUserConversationDeletedAt ??
      conversation?.conversation_deleted_at ??
      conversation?.conversationDeletedAt ??
      null,
  );

const conversationScopesCurrentUserAsVisible = (conversation = null) => {
  if (!conversation || typeof conversation !== 'object') return false;
  if (isGroupConversationRecord(conversation)) {
    return conversationScopesCurrentUserAsActive(conversation);
  }

  const deletedAt = toTimestamp(resolveScopedConversationDeletedAt(conversation));
  if (!deletedAt) return true;

  const latestMessageAt = toTimestamp(
    conversation?.last_message_at ??
      conversation?.lastMessageAt ??
      conversation?.latest_message_at ??
      conversation?.latestMessageAt ??
      null,
  );
  return latestMessageAt > deletedAt;
};

const shouldReplaceParticipants = (existing = null, incoming = null) => {
  if (!Array.isArray(incoming?.participants)) {
    return false;
  }

  const existingMembershipUpdatedAt = toTimestamp(resolveMembershipUpdatedAt(existing));
  const incomingMembershipUpdatedAt = toTimestamp(resolveMembershipUpdatedAt(incoming));
  const incomingAuthoritative = isAuthoritativeParticipantSnapshot(incoming);
  const existingAuthoritative = isAuthoritativeParticipantSnapshot(existing);

  if (incomingMembershipUpdatedAt > 0 && existingMembershipUpdatedAt === 0) {
    return true;
  }

  if (existingMembershipUpdatedAt > 0 && incomingMembershipUpdatedAt === 0) {
    return false;
  }

  if (existingMembershipUpdatedAt > 0 && incomingMembershipUpdatedAt > 0) {
    if (incomingMembershipUpdatedAt > existingMembershipUpdatedAt) return true;
    if (incomingMembershipUpdatedAt < existingMembershipUpdatedAt) return false;
    return incomingAuthoritative || !existingAuthoritative;
  }

  return incomingAuthoritative || !existingAuthoritative;
};

const shouldUseIncomingMembershipMetadata = (existing = null, incoming = null) => {
  if (!incoming || typeof incoming !== 'object') return false;
  if (!existing || typeof existing !== 'object') return true;

  const existingMembershipUpdatedAt = toTimestamp(resolveMembershipUpdatedAt(existing));
  const incomingMembershipUpdatedAt = toTimestamp(resolveMembershipUpdatedAt(incoming));
  const incomingAuthoritative = isAuthoritativeParticipantSnapshot(incoming);
  const existingAuthoritative = isAuthoritativeParticipantSnapshot(existing);

  if (incomingMembershipUpdatedAt > 0 && existingMembershipUpdatedAt === 0) {
    return true;
  }

  if (existingMembershipUpdatedAt > 0 && incomingMembershipUpdatedAt === 0) {
    return false;
  }

  if (existingMembershipUpdatedAt > 0 && incomingMembershipUpdatedAt > 0) {
    if (incomingMembershipUpdatedAt > existingMembershipUpdatedAt) return true;
    if (incomingMembershipUpdatedAt < existingMembershipUpdatedAt) return false;
    return incomingAuthoritative || !existingAuthoritative;
  }

  return incomingAuthoritative || !existingAuthoritative;
};

const readMembershipField = (source = null, snakeKey, camelKey) =>
  source?.[snakeKey] ??
  source?.[camelKey] ??
  source?.group?.[snakeKey] ??
  source?.group?.[camelKey] ??
  null;

const mergeGroupMetadata = (existing = null, incoming = null, useIncomingMembership = true) => {
  const existingGroup = existing && typeof existing === 'object' ? existing : null;
  const incomingGroup = incoming && typeof incoming === 'object' ? incoming : null;
  if (!existingGroup) return incomingGroup;
  if (!incomingGroup) return existingGroup;

  const primary = useIncomingMembership ? incomingGroup : existingGroup;
  const fallback = useIncomingMembership ? existingGroup : incomingGroup;
  return {
    ...fallback,
    ...primary,
    membership_updated_at:
      resolveMembershipUpdatedAt({ group: primary }) ??
      resolveMembershipUpdatedAt({ group: fallback }),
  };
};

const toUnreadCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : 0;
};

const hasOwn = (source = null, key) =>
  Boolean(
    source && typeof source === 'object' && Object.prototype.hasOwnProperty.call(source, key),
  );

const hasConversationAvatarField = (conversation = null) =>
  hasOwn(conversation, 'avatar_url') ||
  hasOwn(conversation, 'avatarUrl') ||
  hasOwn(conversation, 'avatar') ||
  hasOwn(conversation?.group, 'avatar_url') ||
  hasOwn(conversation?.group, 'avatarUrl') ||
  hasOwn(conversation?.group, 'avatar');

const hasConversationTypingField = (conversation = null) =>
  hasOwn(conversation, 'typing') ||
  hasOwn(conversation, 'typingActivity') ||
  hasOwn(conversation, 'typing_activity') ||
  hasOwn(conversation, 'typingUserId') ||
  hasOwn(conversation, 'typing_user_id');

const resolveConversationTypingUserId = (conversation = null) =>
  toStringOrNull(conversation?.typingUserId ?? conversation?.typing_user_id ?? null);

const resolveConversationTypingActivity = (conversation = null) =>
  toStringOrNull(conversation?.typingActivity ?? conversation?.typing_activity ?? null) ||
  (conversation?.typing === true ? 'typing' : null);

const conversationMetadataHasMessageFrontier = (conversation = null) =>
  Boolean(
    toStringOrNull(conversation?.last_server_msg_id ?? null) ||
      toNullableServerSeq(conversation?.last_server_seq ?? null) != null ||
      toStringOrNull(conversation?.last_message_at ?? null),
  );

const mergeConversationTypingMetadata = (existing = null, incoming = null) => {
  if (hasConversationTypingField(incoming)) {
    const typing = incoming?.typing === true;
    const typingUserId = typing
      ? toStringOrNull(incoming?.typingUserId ?? incoming?.typing_user_id ?? null)
      : null;
    const typingActivity = typing ? resolveConversationTypingActivity(incoming) : null;
    return {
      typing,
      typingActivity,
      typing_activity: typingActivity,
      typingUserId,
      typing_user_id: typingUserId,
    };
  }

  const existingTypingUserId = resolveConversationTypingUserId(existing);
  const incomingSenderUserId = toStringOrNull(
    incoming?.last_sender_user_id ?? incoming?.lastSenderUserId ?? null,
  );
  if (
    existingTypingUserId &&
    incomingSenderUserId === existingTypingUserId &&
    conversationMetadataHasMessageFrontier(incoming)
  ) {
    return {
      typing: false,
      typingActivity: null,
      typing_activity: null,
      typingUserId: null,
      typing_user_id: null,
    };
  }

  return {
    typing: existing?.typing === true,
    typingActivity: existing?.typing === true ? resolveConversationTypingActivity(existing) : null,
    typing_activity: existing?.typing === true ? resolveConversationTypingActivity(existing) : null,
    typingUserId: existing?.typing === true ? existingTypingUserId : null,
    typing_user_id: existing?.typing === true ? existingTypingUserId : null,
  };
};

const resolveConversationAvatarValue = (conversation = null) =>
  toStringOrNull(
    conversation?.avatar_url ??
      conversation?.avatarUrl ??
      conversation?.avatar ??
      conversation?.group?.avatar_url ??
      conversation?.group?.avatarUrl ??
      conversation?.group?.avatar ??
      null,
  );

const toNullableServerSeq = toMessengerV2ServerSeq;

const resolveParticipantUserId = (participant = null) =>
  toStringOrNull(participant?.id ?? participant?.user_id ?? null);

const readParticipantReceiptFrontier = (participant = null, kind = 'delivered') => {
  const prefix = kind === 'read' ? 'read' : kind === 'decrypted' ? 'decrypted' : 'delivered';
  const snakePrefix = prefix;
  const atKey = `last_${snakePrefix}_at`;
  const camelAtKey =
    prefix === 'read'
      ? 'lastReadAt'
      : prefix === 'decrypted'
        ? 'lastDecryptedAt'
        : 'lastDeliveredAt';
  const messageIdKey = `last_${snakePrefix}_message_id`;
  const camelMessageIdKey =
    prefix === 'read'
      ? 'lastReadMessageId'
      : prefix === 'decrypted'
        ? 'lastDecryptedMessageId'
        : 'lastDeliveredMessageId';
  const serverMsgIdKey = `last_${snakePrefix}_server_msg_id`;
  const camelServerMsgIdKey =
    prefix === 'read'
      ? 'lastReadServerMsgId'
      : prefix === 'decrypted'
        ? 'lastDecryptedServerMsgId'
        : 'lastDeliveredServerMsgId';
  const serverSeqKey = `last_${snakePrefix}_server_seq`;
  const camelServerSeqKey =
    prefix === 'read'
      ? 'lastReadServerSeq'
      : prefix === 'decrypted'
        ? 'lastDecryptedServerSeq'
        : 'lastDeliveredServerSeq';

  return {
    at: toStringOrNull(participant?.[atKey] ?? participant?.[camelAtKey] ?? null),
    messageId: toStringOrNull(
      participant?.[messageIdKey] ??
        participant?.[camelMessageIdKey] ??
        participant?.[serverMsgIdKey] ??
        participant?.[camelServerMsgIdKey] ??
        null,
    ),
    serverMsgId: toStringOrNull(
      participant?.[serverMsgIdKey] ??
        participant?.[camelServerMsgIdKey] ??
        participant?.[messageIdKey] ??
        participant?.[camelMessageIdKey] ??
        null,
    ),
    serverSeq: toNullableServerSeq(
      participant?.[serverSeqKey] ?? participant?.[camelServerSeqKey] ?? null,
    ),
  };
};

const compareReceiptFrontiers = (left = null, right = null) => {
  const leftHasCursor = Boolean(
    left?.serverMsgId || left?.messageId || toNullableServerSeq(left?.serverSeq ?? null) != null,
  );
  const rightHasCursor = Boolean(
    right?.serverMsgId || right?.messageId || toNullableServerSeq(right?.serverSeq ?? null) != null,
  );
  if (leftHasCursor || rightHasCursor) {
    const comparison = compareMessengerV2ReceiptFrontiers(
      {
        serverMsgId: left?.serverMsgId ?? left?.messageId ?? null,
        serverSeq: left?.serverSeq ?? null,
      },
      {
        serverMsgId: right?.serverMsgId ?? right?.messageId ?? null,
        serverSeq: right?.serverSeq ?? null,
      },
    );
    return comparison ?? -1;
  }

  const leftAt = toTimestamp(left?.at ?? null);
  const rightAt = toTimestamp(right?.at ?? null);
  if (leftAt > 0 || rightAt > 0) {
    if (leftAt <= 0) return -1;
    if (rightAt <= 0) return 1;
    return leftAt - rightAt;
  }

  const leftHasValue = Boolean(left?.serverMsgId || left?.messageId);
  const rightHasValue = Boolean(right?.serverMsgId || right?.messageId);
  if (leftHasValue === rightHasValue) return 0;
  return leftHasValue ? 1 : -1;
};

const mergeReceiptFrontier = (existing = null, incoming = null) => {
  const existingFrontier = existing || {};
  const incomingFrontier = incoming || {};
  const comparison = compareReceiptFrontiers(incomingFrontier, existingFrontier);
  const primary = comparison >= 0 ? incomingFrontier : existingFrontier;
  const fallback = comparison >= 0 ? existingFrontier : incomingFrontier;

  return {
    at: toStringOrNull(primary.at ?? null) ?? toStringOrNull(fallback.at ?? null),
    messageId:
      toStringOrNull(primary.messageId ?? null) ?? toStringOrNull(fallback.messageId ?? null),
    serverMsgId:
      toStringOrNull(primary.serverMsgId ?? null) ?? toStringOrNull(fallback.serverMsgId ?? null),
    serverSeq:
      toNullableServerSeq(primary.serverSeq ?? null) ??
      toNullableServerSeq(fallback.serverSeq ?? null),
  };
};

const applyReceiptFrontierFields = (participant = null, kind = 'delivered', frontier = null) => {
  if (!participant || typeof participant !== 'object') return participant;
  const snakePrefix = kind === 'read' ? 'read' : kind === 'decrypted' ? 'decrypted' : 'delivered';
  return {
    ...participant,
    [`last_${snakePrefix}_at`]: toStringOrNull(frontier?.at ?? null),
    [`last_${snakePrefix}_message_id`]: toStringOrNull(
      frontier?.messageId ?? frontier?.serverMsgId ?? null,
    ),
    [`last_${snakePrefix}_server_msg_id`]: toStringOrNull(
      frontier?.serverMsgId ?? frontier?.messageId ?? null,
    ),
    [`last_${snakePrefix}_server_seq`]: toNullableServerSeq(frontier?.serverSeq ?? null),
  };
};

const mergeParticipantReceiptState = (existing = null, incoming = null) => {
  if (!existing || typeof existing !== 'object') return incoming;
  if (!incoming || typeof incoming !== 'object') return existing;

  let nextParticipant = { ...incoming };
  ['delivered', 'decrypted', 'read'].forEach((kind) => {
    const frontier = mergeReceiptFrontier(
      readParticipantReceiptFrontier(existing, kind),
      readParticipantReceiptFrontier(incoming, kind),
    );
    nextParticipant = applyReceiptFrontierFields(nextParticipant, kind, frontier);
  });
  return nextParticipant;
};

const mergeParticipantsPreservingReceiptState = (
  existingParticipants = [],
  incomingParticipants = [],
) => {
  const incoming = Array.isArray(incomingParticipants) ? incomingParticipants : [];
  const existing = Array.isArray(existingParticipants) ? existingParticipants : [];
  if (incoming.length === 0) return incoming;

  return incoming.map((participant) => {
    const participantUserId = resolveParticipantUserId(participant);
    if (!participantUserId) return participant;
    const existingParticipant =
      existing.find((candidate) => resolveParticipantUserId(candidate) === participantUserId) ??
      null;
    return mergeParticipantReceiptState(existingParticipant, participant);
  });
};

const resolveTimelineIdentity = (item = null) => ({
  id: toStringOrNull(item?.id ?? null),
  clientMsgId: toStringOrNull(item?.clientMsgId ?? item?.client_msg_id ?? null),
  serverSeq: toFiniteServerSeq(item?.serverSeq ?? item?.server_seq ?? null),
});

const isSameTimelineItem = (left = null, right = null) => {
  const leftIdentity = resolveTimelineIdentity(left);
  const rightIdentity = resolveTimelineIdentity(right);

  return (
    (leftIdentity.id && rightIdentity.id && leftIdentity.id === rightIdentity.id) ||
    (leftIdentity.clientMsgId &&
      rightIdentity.clientMsgId &&
      leftIdentity.clientMsgId === rightIdentity.clientMsgId) ||
    (leftIdentity.serverSeq != null &&
      rightIdentity.serverSeq != null &&
      leftIdentity.serverSeq === rightIdentity.serverSeq)
  );
};

const isHiddenTimelineItem = (item = null) => {
  if (!item || typeof item !== 'object') return false;
  if (item.hidden === true || item.silent === true) return true;
  if (
    normalizeContentType(item.contentType ?? item.content_type ?? null) ===
    VOICE_CALL_SIGNAL_CONTENT_TYPE
  ) {
    return true;
  }
  const kind = toStringOrNull(item.messageKind ?? item.message_kind ?? item.kind ?? null);
  if (kind === 'call_signal') return true;
  const body = toStringOrNull(item.body ?? item.rawText ?? item.text ?? null);
  return body === MLS_PENDING_RUNTIME_BODY;
};

const filterRenderableTimelineItems = (items = []) =>
  (Array.isArray(items) ? items : []).filter((item) => !isHiddenTimelineItem(item));

const resolveTimelineItemSortSeq = (item = null) =>
  toFiniteServerSeq(item?.serverSeq ?? item?.server_seq ?? null);

const sortTimelineItems = (items = []) =>
  [...(Array.isArray(items) ? items : [])].sort((left, right) => {
    const leftSeq = resolveTimelineItemSortSeq(left);
    const rightSeq = resolveTimelineItemSortSeq(right);
    if (leftSeq != null && rightSeq != null && leftSeq !== rightSeq) {
      return leftSeq - rightSeq;
    }
    if (leftSeq != null && rightSeq == null) return 1;
    if (leftSeq == null && rightSeq != null) return -1;
    return (
      toTimestamp(left?.createdAt ?? left?.created_at ?? null) -
      toTimestamp(right?.createdAt ?? right?.created_at ?? null)
    );
  });

const mergeTimelineItems = (
  incomingItems = [],
  existingItems = [],
  { preferIncoming = true } = {},
) => {
  const merged = [];
  const lowerPriorityItems = preferIncoming ? existingItems : incomingItems;
  const higherPriorityItems = preferIncoming ? incomingItems : existingItems;
  [lowerPriorityItems, higherPriorityItems]
    .flatMap((items) => filterRenderableTimelineItems(items))
    .forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const existingIndex = merged.findIndex((candidate) => isSameTimelineItem(candidate, item));
      if (existingIndex === -1) {
        merged.push(item);
        return;
      }
      const existing = merged[existingIndex];
      const existingIsOptimistic = existing?.optimistic === true;
      const incomingIsOptimistic = item?.optimistic === true;
      if (existingIsOptimistic !== incomingIsOptimistic) {
        const optimisticItem = existingIsOptimistic ? existing : item;
        const authoritativeItem = existingIsOptimistic ? item : existing;
        merged[existingIndex] = {
          ...optimisticItem,
          ...authoritativeItem,
          optimistic: false,
          failed: authoritativeItem?.failed === true,
          localPendingUpload: false,
          state: toStringOrNull(authoritativeItem?.state ?? null) || 'sent',
        };
        return;
      }
      merged[existingIndex] = { ...existing, ...item };
    });
  return sortTimelineItems(merged);
};

const resolveTimelineFrontier = (currentRecord = null, action = null) => {
  if (action?.replaceTimeline === true) {
    return {
      incomingIsCurrent: true,
      serverMsgId: toStringOrNull(action?.frontierServerMsgId ?? null),
      serverSeq: toNullableServerSeq(action?.frontierServerSeq ?? null),
    };
  }
  const currentServerSeq = toNullableServerSeq(currentRecord?.frontierServerSeq ?? null);
  const incomingServerSeq = toNullableServerSeq(action?.frontierServerSeq ?? null);
  const incomingIsCurrent =
    currentServerSeq == null ||
    (incomingServerSeq != null && incomingServerSeq >= currentServerSeq);

  return {
    incomingIsCurrent,
    serverMsgId: incomingIsCurrent
      ? (toStringOrNull(action?.frontierServerMsgId ?? null) ??
        toStringOrNull(currentRecord?.frontierServerMsgId ?? null))
      : toStringOrNull(currentRecord?.frontierServerMsgId ?? null),
    serverSeq: incomingIsCurrent ? (incomingServerSeq ?? currentServerSeq) : currentServerSeq,
  };
};

const resolveTimelineNamedFrontier = (
  currentRecord = null,
  action = null,
  { messageIdKey, serverSeqKey } = {},
) => {
  if (action?.replaceTimeline === true) {
    return {
      serverMsgId: toStringOrNull(action?.[messageIdKey] ?? null),
      serverSeq: toNullableServerSeq(action?.[serverSeqKey] ?? null),
    };
  }
  const currentServerSeq = toNullableServerSeq(currentRecord?.[serverSeqKey] ?? null);
  const incomingServerSeq = toNullableServerSeq(action?.[serverSeqKey] ?? null);
  const incomingIsCurrent =
    currentServerSeq == null ||
    (incomingServerSeq != null && incomingServerSeq >= currentServerSeq);
  return {
    serverMsgId: incomingIsCurrent
      ? (toStringOrNull(action?.[messageIdKey] ?? null) ??
        toStringOrNull(currentRecord?.[messageIdKey] ?? null))
      : toStringOrNull(currentRecord?.[messageIdKey] ?? null),
    serverSeq: incomingIsCurrent ? (incomingServerSeq ?? currentServerSeq) : currentServerSeq,
  };
};

const resolveOldestTimelineServerSeq = (items = []) => {
  const sorted = sortTimelineItems(items);
  for (const item of sorted) {
    const seq = resolveTimelineItemSortSeq(item);
    if (seq != null) return seq;
  }
  return null;
};

const upsertConversation = (conversations = [], incoming = null) => {
  if (!incoming || typeof incoming !== 'object') {
    return conversations;
  }
  const incomingId = incoming.id != null ? String(incoming.id) : null;
  if (!incomingId) {
    return conversations;
  }
  const existingIndex = conversations.findIndex((item) => String(item?.id) === incomingId);
  const existingConversation = existingIndex === -1 ? null : conversations[existingIndex];
  const nextConversation = {
    ...mergeConversationMetadata(existingConversation, incoming),
    id: incomingId,
  };
  if (existingIndex === -1) {
    return [nextConversation, ...conversations];
  }
  return conversations.map((item, index) =>
    index === existingIndex ? { ...item, ...nextConversation } : item,
  );
};

const upsertCanonicalConversation = (conversations = [], incoming = null) => {
  if (!incoming || typeof incoming !== 'object') {
    return conversations;
  }
  const incomingId = toStringOrNull(incoming.id ?? null);
  if (!incomingId) {
    return conversations;
  }
  const nextConversation = { ...incoming, id: incomingId };
  const source = Array.isArray(conversations) ? conversations : [];
  const existingIndex = source.findIndex((item) => String(item?.id ?? '') === incomingId);
  if (existingIndex === -1) {
    return [nextConversation, ...source];
  }
  return source.map((item, index) => (index === existingIndex ? nextConversation : item));
};

const upsertMergedConversation = (conversations = [], incoming = null) => {
  if (!incoming || typeof incoming !== 'object') {
    return conversations;
  }
  const incomingId = incoming.id != null ? String(incoming.id) : null;
  if (!incomingId) {
    return conversations;
  }
  const existingConversation = (Array.isArray(conversations) ? conversations : []).find(
    (item) => String(item?.id ?? '') === incomingId,
  );
  const nextConversation = mergeConversationMetadata(existingConversation, incoming);
  return upsertConversation(conversations, nextConversation);
};

const resolveConversationFrontierSeq = (conversation = null) =>
  toNullableServerSeq(conversation?.last_server_seq ?? null);

const resolveCurrentUserReadFrontierSeq = (conversation = null) =>
  toNullableServerSeq(
    conversation?.current_user_last_read_server_seq ??
      conversation?.currentUserLastReadServerSeq ??
      null,
  );

const resolveCurrentUserReadFrontierMsgId = (conversation = null) =>
  toStringOrNull(
    conversation?.current_user_last_read_server_msg_id ??
      conversation?.currentUserLastReadServerMsgId ??
      null,
  );

const hasCurrentUserReadFrontier = (conversation = null) =>
  resolveCurrentUserReadFrontierSeq(conversation) != null ||
  Boolean(resolveCurrentUserReadFrontierMsgId(conversation));

const compareCurrentUserReadFrontiers = (existing = null, incoming = null) => {
  const existingSeq = resolveCurrentUserReadFrontierSeq(existing);
  const incomingSeq = resolveCurrentUserReadFrontierSeq(incoming);
  if (existingSeq != null || incomingSeq != null) {
    if (incomingSeq == null) return -1;
    if (existingSeq == null) return 1;
    return Math.sign(incomingSeq - existingSeq);
  }

  const existingMsgId = resolveCurrentUserReadFrontierMsgId(existing);
  const incomingMsgId = resolveCurrentUserReadFrontierMsgId(incoming);
  if (!existingMsgId && !incomingMsgId) return null;
  if (!incomingMsgId) return -1;
  if (!existingMsgId) return 1;
  if (incomingMsgId === existingMsgId) return 0;

  const existingNumericId = Number(existingMsgId);
  const incomingNumericId = Number(incomingMsgId);
  if (Number.isFinite(existingNumericId) && Number.isFinite(incomingNumericId)) {
    return Math.sign(incomingNumericId - existingNumericId);
  }

  return null;
};

const resolveConversationFrontierTime = (conversation = null) =>
  toTimestamp(conversation?.last_message_at ?? conversation?.updated_at ?? null);

const shouldUseIncomingUnreadCount = (existing = null, incoming = null) => {
  if (!incoming || typeof incoming !== 'object' || incoming.unread_count == null) {
    return false;
  }
  if (!existing || typeof existing !== 'object' || existing.unread_count == null) {
    return true;
  }

  const readFrontierComparison = hasCurrentUserReadFrontier(incoming)
    ? compareCurrentUserReadFrontiers(existing, incoming)
    : null;
  if (readFrontierComparison != null && readFrontierComparison !== 0) {
    return readFrontierComparison > 0;
  }

  const existingSeq = resolveConversationFrontierSeq(existing);
  const incomingSeq = resolveConversationFrontierSeq(incoming);
  if (existingSeq != null || incomingSeq != null) {
    if (incomingSeq == null) return false;
    if (existingSeq == null) return true;
    return incomingSeq >= existingSeq;
  }

  const existingTime = resolveConversationFrontierTime(existing);
  const incomingTime = resolveConversationFrontierTime(incoming);
  if (existingTime > 0 || incomingTime > 0) {
    if (incomingTime <= 0) return false;
    if (existingTime <= 0) return true;
    return incomingTime >= existingTime;
  }

  return true;
};

const mergeConversationRecords = (existing = null, incoming = null) => {
  if (!incoming || typeof incoming !== 'object') {
    return existing;
  }
  if (!existing || typeof existing !== 'object') {
    return incoming;
  }

  const existingSeq = resolveConversationFrontierSeq(existing);
  const incomingSeq = resolveConversationFrontierSeq(incoming);
  const existingLastMessageAt = toTimestamp(
    existing.last_message_at ?? existing.updated_at ?? null,
  );
  const incomingLastMessageAt = toTimestamp(
    incoming.last_message_at ?? incoming.updated_at ?? null,
  );
  const preferIncoming =
    existingSeq != null || incomingSeq != null
      ? incomingSeq != null && (existingSeq == null || incomingSeq >= existingSeq)
      : existingLastMessageAt > 0 || incomingLastMessageAt > 0
        ? incomingLastMessageAt > 0 &&
          (existingLastMessageAt <= 0 || incomingLastMessageAt >= existingLastMessageAt)
        : true;
  const readFrontierComparison = compareCurrentUserReadFrontiers(existing, incoming);
  const preferIncomingReadFrontier =
    readFrontierComparison == null
      ? Boolean(resolveCurrentUserReadFrontierMsgId(incoming))
      : readFrontierComparison >= 0;
  const incomingDeclaresGroupAssociation =
    hasOwn(incoming, 'group_id') || hasOwn(incoming, 'groupId') || hasOwn(incoming, 'group');
  const incomingGroupId = toStringOrNull(
    incoming?.group_id ?? incoming?.groupId ?? incoming?.group?.id ?? null,
  );

  return {
    ...existing,
    ...incoming,
    last_server_msg_id:
      preferIncoming || !toStringOrNull(existing.last_server_msg_id)
        ? (toStringOrNull(incoming.last_server_msg_id) ??
          toStringOrNull(existing.last_server_msg_id))
        : toStringOrNull(existing.last_server_msg_id),
    last_message_preview:
      preferIncoming || !toStringOrNull(existing.last_message_preview)
        ? (toStringOrNull(incoming.last_message_preview) ??
          toStringOrNull(existing.last_message_preview))
        : toStringOrNull(existing.last_message_preview),
    subtitle:
      preferIncoming || !toStringOrNull(existing.subtitle)
        ? (toStringOrNull(incoming.subtitle) ?? toStringOrNull(existing.subtitle))
        : toStringOrNull(existing.subtitle),
    last_message_at:
      preferIncoming || !toStringOrNull(existing.last_message_at)
        ? (toStringOrNull(incoming.last_message_at) ?? toStringOrNull(existing.last_message_at))
        : toStringOrNull(existing.last_message_at),
    last_server_seq:
      preferIncoming || toNullableServerSeq(existing.last_server_seq) == null
        ? (toNullableServerSeq(incoming.last_server_seq) ??
          toNullableServerSeq(existing.last_server_seq))
        : toNullableServerSeq(existing.last_server_seq),
    updated_at:
      preferIncoming || !toStringOrNull(existing.updated_at)
        ? (toStringOrNull(incoming.updated_at) ?? toStringOrNull(existing.updated_at))
        : toStringOrNull(existing.updated_at),
    current_user_last_read_server_msg_id: preferIncomingReadFrontier
      ? (resolveCurrentUserReadFrontierMsgId(incoming) ??
        resolveCurrentUserReadFrontierMsgId(existing))
      : resolveCurrentUserReadFrontierMsgId(existing),
    current_user_last_read_server_seq: preferIncomingReadFrontier
      ? (resolveCurrentUserReadFrontierSeq(incoming) ?? resolveCurrentUserReadFrontierSeq(existing))
      : resolveCurrentUserReadFrontierSeq(existing),
    unread_count: shouldUseIncomingUnreadCount(existing, incoming)
      ? toUnreadCount(incoming.unread_count)
      : existing?.unread_count != null
        ? toUnreadCount(existing.unread_count)
        : 0,
    group_id: incomingDeclaresGroupAssociation
      ? incomingGroupId
      : toStringOrNull(existing?.group_id ?? existing?.groupId ?? existing?.group?.id ?? null),
    groupId: incomingDeclaresGroupAssociation
      ? incomingGroupId
      : toStringOrNull(existing?.groupId ?? existing?.group_id ?? existing?.group?.id ?? null),
    group:
      incomingDeclaresGroupAssociation && incomingGroupId == null
        ? null
        : (incoming?.group ?? existing?.group ?? null),
  };
};

const replaceConversations = (existing = [], incoming = [], options = {}) => {
  const source = Array.isArray(incoming) ? incoming : [];
  let next = source.reduce((accumulator, item) => upsertConversation(accumulator, item), []);
  const normalizedExisting = Array.isArray(existing) ? existing : [];
  const authoritativeIds = new Set(
    source.map((item) => toStringOrNull(item?.id ?? null)).filter(Boolean),
  );
  const keepMissingIds = new Set(normalizeConversationIdList(options.keepMissingIds));
  normalizedExisting.forEach((item) => {
    const normalizedId = item?.id != null ? String(item.id) : null;
    if (!normalizedId) return;
    const existingIndex = next.findIndex(
      (candidate) => String(candidate?.id ?? '') === normalizedId,
    );
    if (existingIndex === -1) {
      if (!keepMissingIds.has(normalizedId) && !authoritativeIds.has(normalizedId)) return;
      next = upsertConversation(next, item);
      return;
    }
    next = next.map((candidate, index) =>
      index === existingIndex
        ? mergeConversationMetadata(candidate, mergeConversationRecords(item, candidate))
        : candidate,
    );
  });
  return next;
};

const hasGroupPresenceCounts = (presence = null) =>
  Boolean(
    presence &&
      typeof presence === 'object' &&
      (typeof presence.onlineCount === 'number' ||
        typeof presence.online_count === 'number' ||
        typeof presence.totalCount === 'number' ||
        typeof presence.total_count === 'number'),
  );

const isGroupConversationMetadata = (conversation = null) =>
  Boolean(
    conversation &&
      typeof conversation === 'object' &&
      (conversation.kind === 'group' ||
        conversation.group_id != null ||
        conversation.groupId != null ||
        conversation.participants_authoritative === true ||
        conversation.participantsAuthoritative === true),
  );

const resolveMergedConversationPresence = (existing = null, incoming = null) => {
  const incomingPresence =
    incoming?.presence && typeof incoming.presence === 'object' ? incoming.presence : null;
  const existingPresence =
    existing?.presence && typeof existing.presence === 'object' ? existing.presence : null;
  if (!incomingPresence) {
    return existingPresence;
  }

  const existingState = toStringOrNull(existingPresence?.state ?? null);
  const incomingState = toStringOrNull(incomingPresence?.state ?? null);
  if (
    isGroupConversationMetadata(existing) &&
    existingState === 'group' &&
    !hasGroupPresenceCounts(incomingPresence) &&
    (incomingState === 'online' || incomingState === 'offline')
  ) {
    return existingPresence;
  }

  return incomingPresence;
};

const mergeConversationMetadata = (existing = null, incoming = null) => {
  if (!incoming || typeof incoming !== 'object') {
    return existing;
  }
  if (!existing || typeof existing !== 'object') {
    return incoming;
  }

  // Clearing is monotonic: an older metadata response must not restore cleared history.
  const clearedAt =
    toTimestamp(existing.current_user_cleared_at) > toTimestamp(incoming.current_user_cleared_at)
      ? existing.current_user_cleared_at
      : incoming.current_user_cleared_at;
  const cutoff = toTimestamp(clearedAt);
  if (cutoff) {
    const applyBoundary = (record) => ({
      ...record,
      current_user_cleared_at: clearedAt,
      ...(toTimestamp(record.last_message_at) <= cutoff
        ? {
            last_message_at: null,
            last_message_preview: null,
            last_server_msg_id: null,
            last_server_seq: null,
            subtitle: null,
            unread_count: 0,
          }
        : {}),
    });
    existing = applyBoundary(existing);
    incoming = applyBoundary(incoming);
  }

  const typingMetadata = mergeConversationTypingMetadata(existing, incoming);
  const useIncomingMembership = shouldUseIncomingMembershipMetadata(existing, incoming);
  const membershipSource = useIncomingMembership ? incoming : existing;
  const membershipFallback = useIncomingMembership ? existing : incoming;
  const messageMetadata = mergeConversationRecords(existing, incoming);
  // Pin versions are server UTC timestamps with microseconds, independent of message activity.
  const pinSource =
    String(existing.current_user_pin_updated_at || '') >
    String(incoming.current_user_pin_updated_at || '')
      ? existing
      : incoming;
  const incomingDeclaresGroupAssociation =
    hasOwn(incoming, 'group_id') || hasOwn(incoming, 'groupId') || hasOwn(incoming, 'group');
  const incomingGroupId = toStringOrNull(
    incoming?.group_id ?? incoming?.groupId ?? incoming?.group?.id ?? null,
  );

  return {
    ...existing,
    ...incoming,
    current_user_pinned_at: pinSource.current_user_pinned_at ?? null,
    current_user_pin_updated_at: pinSource.current_user_pin_updated_at ?? null,
    catalog_trusted:
      isCatalogTrustedConversation(existing) || isCatalogTrustedConversation(incoming),
    catalogTrusted:
      isCatalogTrustedConversation(existing) || isCatalogTrustedConversation(incoming),
    last_server_msg_id:
      toStringOrNull(messageMetadata?.last_server_msg_id) ??
      toStringOrNull(existing?.last_server_msg_id),
    last_message_preview:
      toStringOrNull(messageMetadata?.last_message_preview) ??
      toStringOrNull(existing?.last_message_preview),
    subtitle:
      toStringOrNull(messageMetadata?.subtitle) ??
      toStringOrNull(messageMetadata?.last_message_preview) ??
      toStringOrNull(existing?.last_message_preview),
    last_message_at:
      toStringOrNull(messageMetadata?.last_message_at) ?? toStringOrNull(existing?.last_message_at),
    last_server_seq: toNullableServerSeq(messageMetadata?.last_server_seq),
    updated_at: toStringOrNull(messageMetadata?.updated_at) ?? toStringOrNull(existing?.updated_at),
    current_user_last_read_server_msg_id: resolveCurrentUserReadFrontierMsgId(messageMetadata),
    current_user_last_read_server_seq: resolveCurrentUserReadFrontierSeq(messageMetadata),
    membership_updated_at:
      resolveMembershipUpdatedAt(membershipSource) ??
      resolveMembershipUpdatedAt(membershipFallback),
    participants_authoritative:
      readMembershipField(
        membershipSource,
        'participants_authoritative',
        'participantsAuthoritative',
      ) ??
      readMembershipField(
        membershipFallback,
        'participants_authoritative',
        'participantsAuthoritative',
      ),
    current_user_group_membership_status:
      readMembershipField(
        membershipSource,
        'current_user_group_membership_status',
        'currentUserGroupMembershipStatus',
      ) ??
      readMembershipField(
        membershipFallback,
        'current_user_group_membership_status',
        'currentUserGroupMembershipStatus',
      ),
    current_user_group_role:
      readMembershipField(membershipSource, 'current_user_group_role', 'currentUserGroupRole') ??
      readMembershipField(membershipFallback, 'current_user_group_role', 'currentUserGroupRole'),
    current_user_group_member_id:
      readMembershipField(
        membershipSource,
        'current_user_group_member_id',
        'currentUserGroupMemberId',
      ) ??
      readMembershipField(
        membershipFallback,
        'current_user_group_member_id',
        'currentUserGroupMemberId',
      ),
    current_user_group_removed_at:
      readMembershipField(
        membershipSource,
        'current_user_group_removed_at',
        'currentUserGroupRemovedAt',
      ) ??
      readMembershipField(
        membershipFallback,
        'current_user_group_removed_at',
        'currentUserGroupRemovedAt',
      ),
    group_id: incomingDeclaresGroupAssociation
      ? incomingGroupId
      : toStringOrNull(existing?.group_id ?? existing?.groupId ?? existing?.group?.id ?? null),
    groupId: incomingDeclaresGroupAssociation
      ? incomingGroupId
      : toStringOrNull(existing?.groupId ?? existing?.group_id ?? existing?.group?.id ?? null),
    group:
      incomingDeclaresGroupAssociation && incomingGroupId == null
        ? null
        : mergeGroupMetadata(existing?.group, incoming?.group, useIncomingMembership),
    avatar_url: hasConversationAvatarField(incoming)
      ? resolveConversationAvatarValue(incoming)
      : resolveConversationAvatarValue(existing),
    unread_count: toUnreadCount(messageMetadata?.unread_count ?? 0),
    participants: shouldReplaceParticipants(existing, incoming)
      ? mergeParticipantsPreservingReceiptState(existing?.participants, incoming.participants)
      : Array.isArray(existing?.participants)
        ? existing.participants
        : [],
    presence: resolveMergedConversationPresence(existing, incoming),
    typing: typingMetadata.typing,
    typingActivity: typingMetadata.typingActivity,
    typing_activity: typingMetadata.typing_activity,
    typingUserId: typingMetadata.typingUserId,
    typing_user_id: typingMetadata.typing_user_id,
  };
};

const sanitizeThreadRecord = (threadRecord = null) => {
  if (!threadRecord || typeof threadRecord !== 'object') {
    return createThreadState('idle');
  }
  const normalizedReadyInfo = normalizeReadyInfo(
    threadRecord.readyInfo,
    threadRecord.phase === 'ready',
  );
  const normalizedStale = threadRecord.stale === true;
  const normalizedSyncing = threadRecord.syncing === true;
  if (
    threadRecord.conversation == null &&
    threadRecord.stale === normalizedStale &&
    threadRecord.syncing === normalizedSyncing &&
    threadRecord.readyInfo === normalizedReadyInfo
  ) {
    return threadRecord;
  }
  return {
    ...threadRecord,
    conversation: null,
    stale: normalizedStale,
    syncing: normalizedSyncing,
    readyInfo: normalizedReadyInfo,
  };
};

const syncMembershipReadyInfoWithCanonicalConversation = (
  threadRecord = null,
  canonicalConversation = null,
) => {
  if (!threadRecord) return threadRecord;
  if (!conversationScopesCurrentUserAsActive(canonicalConversation)) {
    return sanitizeThreadRecord(threadRecord);
  }

  const readinessReason = toStringOrNull(
    threadRecord.readyInfo?.readinessReason ?? threadRecord.readyInfo?.readiness_reason ?? null,
  );
  const readinessState = toStringOrNull(
    threadRecord.readyInfo?.readinessState ?? threadRecord.readyInfo?.readiness_state ?? null,
  );
  const staleMembershipReason = ['former_member', 'membership_changed'].includes(readinessReason);
  const staleMembershipState = readinessState === 'read_only';
  const staleReadOnlyFlag =
    threadRecord.readyInfo?.readOnly === true ||
    threadRecord.readyInfo?.read_only === true ||
    threadRecord.readyInfo?.formerMember === true ||
    threadRecord.readyInfo?.former_member === true;
  if (!staleMembershipReason && !staleMembershipState && !staleReadOnlyFlag) {
    return threadRecord;
  }

  return {
    ...threadRecord,
    conversation: null,
    readyInfo: normalizeReadyInfo(
      {
        ...threadRecord.readyInfo,
        readOnly: false,
        read_only: false,
        formerMember: false,
        former_member: false,
        readinessReason: null,
        readiness_reason: null,
        readinessState: null,
        readiness_state: null,
        serverReadinessState: null,
        server_readiness_state: null,
        serverReadinessReason: null,
        server_readiness_reason: null,
        serverSendCapable: null,
        server_send_capable: null,
      },
      threadRecord.readyInfo?.ready === true,
    ),
  };
};

const syncThreadConversationMetadata = (threadRecord = null, conversation = null) => {
  if (!threadRecord || !conversation) return threadRecord;
  return syncMembershipReadyInfoWithCanonicalConversation(
    {
      ...threadRecord,
      conversation: null,
      stale: false,
      error: null,
    },
    conversation,
  );
};

const findConversationById = (conversations = [], conversationId = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) return null;
  return (
    (Array.isArray(conversations) ? conversations : []).find(
      (conversation) => String(conversation?.id ?? '') === normalizedConversationId,
    ) ?? null
  );
};

const resolveCanonicalConversation = (state, conversationId = null, incoming = null) => {
  const normalizedConversationId =
    toStringOrNull(conversationId) ?? toStringOrNull(incoming?.id ?? null);
  if (!normalizedConversationId) return null;

  let canonical = null;
  canonical = mergeConversationMetadata(
    canonical,
    findConversationById(state?.conversations, normalizedConversationId),
  );
  canonical = mergeConversationMetadata(canonical, incoming);

  if (!canonical || typeof canonical !== 'object') return null;
  return {
    ...canonical,
    id: toStringOrNull(canonical.id ?? normalizedConversationId) || normalizedConversationId,
  };
};

const createActiveMembershipThreadRecord = (threadRecord = null, conversation = null) => {
  const syncedThread = syncThreadConversationMetadata(threadRecord, conversation);
  if (!syncedThread) return syncedThread;
  return syncMembershipReadyInfoWithCanonicalConversation(syncedThread, conversation);
};

const normalizeConversationUnread = (conversation = null) => {
  if (!conversation || typeof conversation !== 'object') {
    return conversation;
  }

  const conversationId = toStringOrNull(conversation?.id ?? null);
  if (!conversationId) {
    return conversation;
  }

  const lastServerMsgId = toStringOrNull(conversation?.last_server_msg_id ?? null);
  const lastServerSeq = resolveConversationFrontierSeq(conversation);
  const currentUserReadSeq = resolveCurrentUserReadFrontierSeq(conversation);
  const currentUserReadMsgId = resolveCurrentUserReadFrontierMsgId(conversation);
  const readCurrentFrontier =
    (lastServerSeq != null && currentUserReadSeq != null && currentUserReadSeq >= lastServerSeq) ||
    (Boolean(lastServerMsgId) &&
      Boolean(currentUserReadMsgId) &&
      lastServerMsgId === currentUserReadMsgId);

  return {
    ...conversation,
    unread_count: readCurrentFrontier ? 0 : toUnreadCount(conversation?.unread_count ?? 0),
  };
};

const normalizeConversationsUnread = (conversations = []) =>
  (Array.isArray(conversations) ? conversations : []).map((conversation) =>
    normalizeConversationUnread(conversation),
  );

const seedConversation = (conversations = [], conversationId = null) => {
  const normalizedConversationId = conversationId != null ? String(conversationId).trim() : null;
  if (!normalizedConversationId) {
    return conversations;
  }
  if (
    (Array.isArray(conversations) ? conversations : []).some(
      (conversation) => toStringOrNull(conversation?.id ?? null) === normalizedConversationId,
    )
  ) {
    return conversations;
  }
  return upsertConversation(
    conversations,
    markConversationCatalogTrusted({ id: normalizedConversationId }, false),
  );
};

const upsertTimelineItem = (items = [], incoming = null) => {
  if (!incoming || typeof incoming !== 'object') {
    return filterRenderableTimelineItems(items);
  }
  const sourceItems = filterRenderableTimelineItems(items);
  if (isHiddenTimelineItem(incoming)) {
    return sourceItems;
  }
  const existingIndex = sourceItems.findIndex((item) => isSameTimelineItem(item, incoming));

  if (existingIndex === -1) {
    return [...sourceItems, incoming];
  }

  return sourceItems.map((item, index) =>
    index === existingIndex ? { ...item, ...incoming } : item,
  );
};

const resolveConversationSummaryItem = (items = []) => {
  const source = filterRenderableTimelineItems(items);
  return [...source].reverse().find((item) => {
    const body = toStringOrNull(item?.body ?? null);
    const linkPreview =
      item?.linkPreview && typeof item.linkPreview === 'object' ? item.linkPreview : null;
    const hasRenderableBody = body && body !== 'No content' && body !== MLS_PENDING_RUNTIME_BODY;
    const hasRenderablePreview = Boolean(
      linkPreview?.url || linkPreview?.title || linkPreview?.description || linkPreview?.siteName,
    );
    return hasRenderableBody || hasRenderablePreview;
  });
};

const stripUrlsFromText = (value = null) => {
  const normalized = toStringOrNull(value);
  if (!normalized) return '';

  return normalized
    .replace(/((?:https?:\/\/|www\.)[^\s<]+)/gi, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?;:])/g, '$1')
    .trim();
};

const normalizeTimelineLinkPreview = (linkPreview = null) =>
  linkPreview && typeof linkPreview === 'object'
    ? {
        ...linkPreview,
        url: toStringOrNull(linkPreview?.url ?? null),
        siteName: toStringOrNull(linkPreview?.siteName ?? null),
        title: toStringOrNull(linkPreview?.title ?? null),
        description: toStringOrNull(linkPreview?.description ?? null),
        imageUrl: toStringOrNull(linkPreview?.imageUrl ?? null),
        type: toStringOrNull(linkPreview?.type ?? null),
        groupId: toStringOrNull(linkPreview?.groupId ?? linkPreview?.group_id ?? null),
        groupName: toStringOrNull(linkPreview?.groupName ?? linkPreview?.group_name ?? null),
        groupAvatarUrl: toStringOrNull(
          linkPreview?.groupAvatarUrl ??
            linkPreview?.group_avatar_url ??
            linkPreview?.avatarUrl ??
            linkPreview?.avatar_url ??
            null,
        ),
        alreadyMember: Boolean(linkPreview?.alreadyMember ?? linkPreview?.already_member),
        groupInviteStatus: toStringOrNull(
          linkPreview?.groupInviteStatus ??
            linkPreview?.group_invite_status ??
            linkPreview?.status ??
            null,
        ),
        groupInviteReason: toStringOrNull(
          linkPreview?.groupInviteReason ??
            linkPreview?.group_invite_reason ??
            linkPreview?.reason ??
            null,
        ),
        groupInviteJoinable: toBooleanOrNull(
          linkPreview?.groupInviteJoinable ??
            linkPreview?.group_invite_joinable ??
            linkPreview?.joinable ??
            null,
        ),
        groupInviteExpired: toBooleanOrNull(
          linkPreview?.groupInviteExpired ??
            linkPreview?.group_invite_expired ??
            linkPreview?.expired ??
            null,
        ),
        groupInviteExpiresAt: toStringOrNull(
          linkPreview?.groupInviteExpiresAt ??
            linkPreview?.group_invite_expires_at ??
            linkPreview?.expiresAt ??
            linkPreview?.expires_at ??
            null,
        ),
        groupInviteMemberCount: toNonNegativeIntegerOrNull(
          linkPreview?.groupInviteMemberCount ??
            linkPreview?.group_invite_member_count ??
            linkPreview?.memberCount ??
            linkPreview?.member_count ??
            null,
        ),
        groupInviteMaxMembers: toNonNegativeIntegerOrNull(
          linkPreview?.groupInviteMaxMembers ??
            linkPreview?.group_invite_max_members ??
            linkPreview?.maxMembers ??
            linkPreview?.max_members ??
            null,
        ),
      }
    : null;

const patchConversationSummary = (conversations = [], conversationId = null, item = null) => {
  const normalizedConversationId = conversationId != null ? String(conversationId).trim() : null;
  if (!normalizedConversationId) {
    return conversations;
  }

  if (!item || typeof item !== 'object') {
    return conversations;
  }

  const summaryBody =
    resolveMessengerV2GroupInviteSummaryText(item?.linkPreview ?? null) ||
    (item?.linkPreview
      ? stripUrlsFromText(item.body ?? null)
      : toStringOrNull(item.body ?? null)) ||
    toStringOrNull(item?.linkPreview?.title ?? null) ||
    toStringOrNull(item?.linkPreview?.description ?? null) ||
    toStringOrNull(item?.linkPreview?.siteName ?? null) ||
    null;
  if (!summaryBody) {
    return conversations;
  }

  const summaryTime = toStringOrNull(item.createdAt ?? item.created_at ?? null) || null;

  return upsertConversation(conversations, {
    id: normalizedConversationId,
    last_message_preview: summaryBody,
    subtitle: summaryBody,
    last_message_at: summaryTime,
    updated_at: summaryTime,
  });
};

const applyTimelineEdit = (
  items = [],
  target = null,
  body = null,
  editedAt = null,
  linkPreview = null,
) => {
  const normalizedTargetId = resolveTimelineIdentity(target);
  const normalizedBody = toStringOrNull(body);
  if (!normalizedBody) {
    return items;
  }
  const normalizedLinkPreview = normalizeTimelineLinkPreview(linkPreview);
  const nextMessageKind = normalizedLinkPreview?.url ? 'link_preview' : 'text';

  return (Array.isArray(items) ? items : []).map((item) => {
    if (
      (normalizedTargetId.id && toStringOrNull(item?.id ?? null) === normalizedTargetId.id) ||
      (normalizedTargetId.clientMsgId &&
        toStringOrNull(item?.clientMsgId ?? item?.client_msg_id ?? null) ===
          normalizedTargetId.clientMsgId) ||
      (normalizedTargetId.serverSeq != null &&
        toFiniteServerSeq(item?.serverSeq ?? item?.server_seq ?? null) ===
          normalizedTargetId.serverSeq)
    ) {
      return {
        ...item,
        body: normalizedBody,
        rawText: normalizedBody,
        messageKind: nextMessageKind,
        linkPreview: normalizedLinkPreview,
        edited: true,
        editedAt: toStringOrNull(editedAt ?? null) || item?.editedAt || null,
      };
    }
    return item;
  });
};

const applyTimelineDelete = (items = [], target = null) => {
  const normalizedTargetId = resolveTimelineIdentity(target);
  return (Array.isArray(items) ? items : []).filter((item) => {
    const itemId = toStringOrNull(item?.id ?? null);
    const itemClientMsgId = toStringOrNull(item?.clientMsgId ?? item?.client_msg_id ?? null);
    const itemServerSeq = toFiniteServerSeq(item?.serverSeq ?? item?.server_seq ?? null);

    if (normalizedTargetId.id && itemId === normalizedTargetId.id) {
      return false;
    }
    if (normalizedTargetId.clientMsgId && itemClientMsgId === normalizedTargetId.clientMsgId) {
      return false;
    }
    if (normalizedTargetId.serverSeq != null && itemServerSeq === normalizedTargetId.serverSeq) {
      return false;
    }
    return true;
  });
};

const applyTimelineDeletes = (items = [], targets = []) => {
  return (Array.isArray(targets) ? targets : []).reduce(
    (nextItems, target) => applyTimelineDelete(nextItems, target),
    Array.isArray(items) ? items : [],
  );
};

const patchConversationTyping = (
  conversations = [],
  conversationId = null,
  typing = false,
  typingUserId = null,
  typingActivity = null,
) => {
  const normalizedConversationId = conversationId != null ? String(conversationId).trim() : null;
  if (!normalizedConversationId) {
    return conversations;
  }
  const normalizedTypingUserId = typing === true ? toStringOrNull(typingUserId) : null;
  const normalizedTypingActivity =
    typing === true ? toStringOrNull(typingActivity) || 'typing' : null;

  return upsertConversation(conversations, {
    id: normalizedConversationId,
    typing: typing === true,
    typingActivity: normalizedTypingActivity,
    typing_activity: normalizedTypingActivity,
    typingUserId: normalizedTypingUserId,
    typing_user_id: normalizedTypingUserId,
  });
};

const patchConversationPresence = (conversations = [], conversationId = null, presence = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId || !presence || typeof presence !== 'object') {
    return conversations;
  }

  const existingConversation = (Array.isArray(conversations) ? conversations : []).find(
    (item) => toStringOrNull(item?.id ?? null) === normalizedConversationId,
  );
  const isGroupConversation =
    existingConversation?.kind === 'group' ||
    existingConversation?.group_id != null ||
    existingConversation?.groupId != null;
  const existingPresence = existingConversation?.presence;
  const incomingHasGroupCounts =
    typeof presence.onlineCount === 'number' ||
    typeof presence.online_count === 'number' ||
    typeof presence.totalCount === 'number' ||
    typeof presence.total_count === 'number';
  const incomingState = toStringOrNull(presence.state ?? null);
  const existingState = toStringOrNull(existingPresence?.state ?? null);
  if (
    isGroupConversation &&
    existingState === 'group' &&
    !incomingHasGroupCounts &&
    (incomingState === 'online' || incomingState === 'offline')
  ) {
    return conversations;
  }

  return upsertConversation(conversations, {
    id: normalizedConversationId,
    presence: {
      ...(presence || {}),
    },
  });
};

const patchConversationParticipantsReceipt = (
  conversations = [],
  conversationId = null,
  userId = null,
  lastDeliveredAt = null,
  lastReadAt = null,
  lastDeliveredMessageId = null,
  lastReadMessageId = null,
  lastDeliveredServerMsgId = null,
  lastDecryptedServerMsgId = null,
  lastReadServerMsgId = null,
  lastDeliveredServerSeq = null,
  lastDecryptedServerSeq = null,
  lastReadServerSeq = null,
) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedUserId = toStringOrNull(userId);
  if (!normalizedConversationId || !normalizedUserId) {
    return conversations;
  }

  let found = false;
  const nextConversations = (Array.isArray(conversations) ? conversations : []).map(
    (conversation) => {
      if (String(conversation?.id ?? '') !== normalizedConversationId) {
        return conversation;
      }
      found = true;
      return applyParticipantReceiptPatch(conversation, {
        userId: normalizedUserId,
        lastDeliveredAt,
        lastReadAt,
        lastDeliveredMessageId,
        lastReadMessageId,
        lastDeliveredServerMsgId,
        lastDecryptedServerMsgId,
        lastReadServerMsgId,
        lastDeliveredServerSeq,
        lastDecryptedServerSeq,
        lastReadServerSeq,
      });
    },
  );

  if (found) {
    return nextConversations;
  }

  return upsertConversation(nextConversations, {
    id: normalizedConversationId,
    participants: [],
  });
};

const applyParticipantReceiptPatch = (conversation = null, patch = null) => {
  if (!conversation || typeof conversation !== 'object' || !patch || typeof patch !== 'object') {
    return conversation;
  }

  const normalizedUserId = toStringOrNull(patch.userId);
  if (!normalizedUserId) {
    return conversation;
  }

  const participants = Array.isArray(conversation.participants) ? conversation.participants : [];
  if (participants.length === 0) {
    return conversation;
  }

  const nextParticipants = participants.map((participant) => {
    const participantUserId = toStringOrNull(participant?.id ?? participant?.user_id ?? null);
    if (participantUserId !== normalizedUserId) {
      return participant;
    }

    const patchedParticipant = {
      ...participant,
      last_delivered_at:
        toStringOrNull(patch.lastDeliveredAt) ??
        toStringOrNull(participant?.last_delivered_at ?? participant?.lastDeliveredAt ?? null) ??
        null,
      last_read_at:
        toStringOrNull(patch.lastReadAt) ??
        toStringOrNull(participant?.last_read_at ?? participant?.lastReadAt ?? null) ??
        null,
      last_delivered_message_id:
        toStringOrNull(patch.lastDeliveredMessageId) ??
        toStringOrNull(
          participant?.last_delivered_message_id ?? participant?.lastDeliveredMessageId ?? null,
        ) ??
        null,
      last_read_message_id:
        toStringOrNull(patch.lastReadMessageId) ??
        toStringOrNull(
          participant?.last_read_message_id ?? participant?.lastReadMessageId ?? null,
        ) ??
        null,
      last_delivered_server_msg_id:
        toStringOrNull(patch.lastDeliveredServerMsgId ?? patch.lastDeliveredMessageId) ??
        toStringOrNull(
          participant?.last_delivered_server_msg_id ??
            participant?.lastDeliveredServerMsgId ??
            participant?.last_delivered_message_id ??
            participant?.lastDeliveredMessageId ??
            null,
        ) ??
        null,
      last_decrypted_server_msg_id:
        toStringOrNull(patch.lastDecryptedServerMsgId) ??
        toStringOrNull(
          participant?.last_decrypted_server_msg_id ??
            participant?.lastDecryptedServerMsgId ??
            null,
        ) ??
        null,
      last_read_server_msg_id:
        toStringOrNull(patch.lastReadServerMsgId ?? patch.lastReadMessageId) ??
        toStringOrNull(
          participant?.last_read_server_msg_id ??
            participant?.lastReadServerMsgId ??
            participant?.last_read_message_id ??
            participant?.lastReadMessageId ??
            null,
        ) ??
        null,
      last_delivered_server_seq:
        toNullableServerSeq(patch.lastDeliveredServerSeq) ??
        toNullableServerSeq(
          participant?.last_delivered_server_seq ?? participant?.lastDeliveredServerSeq ?? null,
        ),
      last_decrypted_server_seq:
        toNullableServerSeq(patch.lastDecryptedServerSeq) ??
        toNullableServerSeq(
          participant?.last_decrypted_server_seq ?? participant?.lastDecryptedServerSeq ?? null,
        ),
      last_read_server_seq:
        toNullableServerSeq(patch.lastReadServerSeq) ??
        toNullableServerSeq(
          participant?.last_read_server_seq ?? participant?.lastReadServerSeq ?? null,
        ),
    };

    return mergeParticipantReceiptState(participant, patchedParticipant);
  });

  return {
    ...conversation,
    participants: nextParticipants,
  };
};

const removeConversationParticipantByUserId = (conversation = null, userId = null) => {
  if (!conversation || typeof conversation !== 'object') {
    return conversation;
  }

  const normalizedUserId = toStringOrNull(userId);
  if (!normalizedUserId) {
    return conversation;
  }

  const participants = Array.isArray(conversation.participants) ? conversation.participants : [];
  if (participants.length === 0) {
    return conversation;
  }

  const nextParticipants = participants.filter((participant) => {
    const participantIds = [
      participant?.id,
      participant?.user_id,
      participant?.userId,
      participant?.user?.id,
    ]
      .map(toStringOrNull)
      .filter(Boolean);
    return !participantIds.includes(normalizedUserId);
  });

  if (nextParticipants.length === participants.length) {
    return conversation;
  }

  return {
    ...conversation,
    participants: nextParticipants,
    presence:
      conversation.presence && typeof conversation.presence === 'object'
        ? {
            ...conversation.presence,
            totalCount:
              typeof conversation.presence.totalCount === 'number'
                ? Math.max(0, conversation.presence.totalCount - 1)
                : typeof conversation.presence.total_count === 'number'
                  ? Math.max(0, conversation.presence.total_count - 1)
                  : conversation.presence.totalCount,
            total_count:
              typeof conversation.presence.total_count === 'number'
                ? Math.max(0, conversation.presence.total_count - 1)
                : conversation.presence.total_count,
          }
        : conversation.presence,
  };
};

const createFormerMemberReadyInfo = (currentReadyInfo = null, conversation = null) =>
  normalizeReadyInfo(
    {
      ...(currentReadyInfo && typeof currentReadyInfo === 'object' ? currentReadyInfo : {}),
      groupId:
        currentReadyInfo?.groupId ??
        currentReadyInfo?.group_id ??
        conversation?.mls_group_id ??
        conversation?.meta?.mls_group_id ??
        null,
      group_id:
        currentReadyInfo?.group_id ??
        currentReadyInfo?.groupId ??
        conversation?.mls_group_id ??
        conversation?.meta?.mls_group_id ??
        null,
      ready: false,
      sendCapable: false,
      send_capable: false,
      readyToSend: false,
      ready_to_send: false,
      readOnly: true,
      read_only: true,
      readinessState: 'read_only',
      readiness_state: 'read_only',
      readinessReason: 'former_member',
      readiness_reason: 'former_member',
    },
    false,
  );

const isActiveConversationAction = (state, action) => {
  if (!action?.conversationId) return true;
  return String(action.conversationId) === String(state.activeConversationId ?? '');
};

const normalizeReadyInfo = (readyInfo = null, ready = false) =>
  normalizeMessengerV2ReadyInfo(readyInfo, ready);

const createThreadState = (phase = 'empty') => ({
  phase,
  conversation: null,
  snapshot: null,
  readyInfo: null,
  stale: false,
  syncing: false,
  error: null,
});

const rehydrateRestoredThreadRecord = (record = null) => {
  if (!record || typeof record !== 'object') {
    return createThreadState('idle');
  }

  return sanitizeThreadRecord({
    ...record,
    phase: record.phase === 'ready' ? 'pending' : (record.phase ?? 'idle'),
    readyInfo: normalizeReadyInfo(record.readyInfo, false),
    stale: true,
    syncing: false,
    error: null,
  });
};

const createTimelineState = (phase = 'empty') => ({
  phase,
  items: [],
  groupId: null,
  frontierServerMsgId: null,
  frontierServerSeq: null,
  applicationFrontierServerMsgId: null,
  applicationFrontierServerSeq: null,
  remoteDeliveredServerMsgId: null,
  remoteDeliveredServerSeq: null,
  remoteDecryptedServerMsgId: null,
  remoteDecryptedServerSeq: null,
  unresolvedServerSeqs: [],
  historyPhase: 'idle',
  historyBeforeSeq: null,
  historyHasMore: true,
  historyError: null,
  cleared: false,
  stale: false,
  error: null,
});

const EMPTY_THREAD_STATE = createThreadState('empty');
const EMPTY_TIMELINE_STATE = createTimelineState('empty');

const sanitizeTimelineRecord = (timelineRecord = null) => {
  if (!timelineRecord || typeof timelineRecord !== 'object') {
    return createTimelineState('idle');
  }
  const normalizedRecord = {
    ...createTimelineState(timelineRecord.phase || 'idle'),
    ...timelineRecord,
    items: Array.isArray(timelineRecord.items) ? timelineRecord.items : [],
    unresolvedServerSeqs: Array.isArray(timelineRecord.unresolvedServerSeqs)
      ? timelineRecord.unresolvedServerSeqs
      : [],
    cleared: timelineRecord.cleared === true,
    stale: timelineRecord.stale === true,
  };
  const normalizedKeys = Object.keys(normalizedRecord);
  const currentKeys = Object.keys(timelineRecord);
  const isAlreadyNormalized =
    normalizedKeys.length === currentKeys.length &&
    normalizedKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(timelineRecord, key) &&
        Object.is(timelineRecord[key], normalizedRecord[key]),
    );
  return isAlreadyNormalized ? timelineRecord : normalizedRecord;
};

const getThreadRecord = (state, conversationId = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    return createThreadState('empty');
  }
  const record =
    state.threadsByConversation?.[normalizedConversationId] ?? createThreadState('idle');
  return sanitizeThreadRecord(record);
};

const reconcileActiveThreadWithCanonicalConversation = (state) => {
  const activeConversationId = toStringOrNull(state?.activeConversationId ?? null);
  if (!activeConversationId) {
    return state;
  }

  const activeThread = getThreadRecord(state, activeConversationId);
  return {
    ...state,
    threadsByConversation: updateThreadRecord(state, activeConversationId, activeThread),
  };
};

const getTimelineRecord = (state, conversationId = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    return createTimelineState('empty');
  }
  return sanitizeTimelineRecord(state.timelinesByConversation?.[normalizedConversationId]);
};

const updateThreadRecord = (state, conversationId = null, nextRecord = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId || !nextRecord) {
    return sanitizeThreadsByConversation(state.threadsByConversation);
  }
  const currentRecords = sanitizeThreadsByConversation(state.threadsByConversation);
  const normalizedRecord = sanitizeThreadRecord(nextRecord);
  if (currentRecords[normalizedConversationId] === normalizedRecord) return currentRecords;
  return { ...currentRecords, [normalizedConversationId]: normalizedRecord };
};

const sanitizeThreadsByConversation = (threadsByConversation = {}) => {
  if (!threadsByConversation || typeof threadsByConversation !== 'object') {
    return {};
  }
  let changed = false;
  const normalizedRecords = Object.entries(threadsByConversation).reduce(
    (acc, [conversationId, threadRecord]) => {
      const normalizedConversationId = toStringOrNull(conversationId);
      if (!normalizedConversationId) {
        changed = true;
        return acc;
      }
      const normalizedRecord = sanitizeThreadRecord(threadRecord);
      if (normalizedConversationId !== conversationId || normalizedRecord !== threadRecord) {
        changed = true;
      }
      acc[normalizedConversationId] = normalizedRecord;
      return acc;
    },
    {},
  );
  return changed ? normalizedRecords : threadsByConversation;
};

const sanitizeTimelinesByConversation = (timelinesByConversation = {}) => {
  if (!timelinesByConversation || typeof timelinesByConversation !== 'object') return {};
  let changed = false;
  const normalizedRecords = Object.entries(timelinesByConversation).reduce(
    (acc, [conversationId, timelineRecord]) => {
      const normalizedConversationId = toStringOrNull(conversationId);
      if (!normalizedConversationId) {
        changed = true;
        return acc;
      }
      const normalizedRecord = sanitizeTimelineRecord(timelineRecord);
      if (normalizedConversationId !== conversationId || normalizedRecord !== timelineRecord) {
        changed = true;
      }
      acc[normalizedConversationId] = normalizedRecord;
      return acc;
    },
    {},
  );
  return changed ? normalizedRecords : timelinesByConversation;
};

const enforceThreadMetadataBoundary = (state) => {
  if (!state || typeof state !== 'object') {
    return state;
  }
  const activeConversationId = toStringOrNull(state.activeConversationId ?? null);
  const {
    activeThread: legacyActiveThread,
    timeline: legacyActiveTimeline,
    threadsByConversation: rawThreadsByConversation,
    timelinesByConversation: rawTimelinesByConversation,
    ...baseState
  } = state;
  let threadsByConversation = sanitizeThreadsByConversation(rawThreadsByConversation);
  let timelinesByConversation = sanitizeTimelinesByConversation(rawTimelinesByConversation);
  const canUseLegacyActiveThread =
    !rawThreadsByConversation ||
    typeof rawThreadsByConversation !== 'object' ||
    Object.keys(rawThreadsByConversation).length === 0;
  const canUseLegacyActiveTimeline =
    !rawTimelinesByConversation ||
    typeof rawTimelinesByConversation !== 'object' ||
    Object.keys(rawTimelinesByConversation).length === 0;

  if (activeConversationId) {
    if (!threadsByConversation[activeConversationId]) {
      threadsByConversation = {
        ...threadsByConversation,
        [activeConversationId]:
          canUseLegacyActiveThread && legacyActiveThread
            ? sanitizeThreadRecord(legacyActiveThread)
            : createThreadState('idle'),
      };
    }
    if (!timelinesByConversation[activeConversationId]) {
      timelinesByConversation = {
        ...timelinesByConversation,
        [activeConversationId]:
          canUseLegacyActiveTimeline && legacyActiveTimeline
            ? sanitizeTimelineRecord(legacyActiveTimeline)
            : createTimelineState('idle'),
      };
    }
  }

  // Apply the account-wide history boundary after restoring any legacy timeline too.
  for (const conversation of state.conversations || []) {
    const cutoff = toTimestamp(conversation.current_user_cleared_at);
    const record = timelinesByConversation[conversation.id];
    if (!cutoff || !record?.items?.length) continue;
    const items = record.items.filter((item) => {
      const createdAt = toTimestamp(item.createdAt ?? item.created_at);
      return createdAt > cutoff || (!createdAt && item.optimistic === true);
    });
    if (items.length !== record.items.length) {
      timelinesByConversation = {
        ...timelinesByConversation,
        [conversation.id]: { ...record, items },
      };
    }
  }

  const activeThread = activeConversationId
    ? threadsByConversation[activeConversationId]
    : EMPTY_THREAD_STATE;
  const timeline = activeConversationId
    ? timelinesByConversation[activeConversationId]
    : EMPTY_TIMELINE_STATE;
  const activeThreadDescriptor = Object.getOwnPropertyDescriptor(state, 'activeThread');
  const timelineDescriptor = Object.getOwnPropertyDescriptor(state, 'timeline');
  if (
    threadsByConversation === rawThreadsByConversation &&
    timelinesByConversation === rawTimelinesByConversation &&
    legacyActiveThread === activeThread &&
    legacyActiveTimeline === timeline &&
    typeof activeThreadDescriptor?.get === 'function' &&
    typeof timelineDescriptor?.get === 'function'
  ) {
    return state;
  }
  const nextState = {
    ...baseState,
    threadsByConversation,
    timelinesByConversation,
  };

  Object.defineProperties(nextState, {
    activeThread: {
      enumerable: true,
      configurable: true,
      get: () => activeThread,
    },
    timeline: {
      enumerable: true,
      configurable: true,
      get: () => timeline,
    },
  });

  return nextState;
};

const updateTimelineRecord = (state, conversationId = null, nextRecord = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId || !nextRecord) {
    return state.timelinesByConversation || {};
  }
  const currentRecords = state.timelinesByConversation || {};
  const normalizedRecord = sanitizeTimelineRecord(nextRecord);
  if (currentRecords[normalizedConversationId] === normalizedRecord) return currentRecords;
  return { ...currentRecords, [normalizedConversationId]: normalizedRecord };
};

const getHiddenTargetsRecord = (state, conversationId = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    return [];
  }
  const hiddenTargets = state.hiddenTargetsByConversation?.[normalizedConversationId];
  return Array.isArray(hiddenTargets) ? hiddenTargets : [];
};

const updateHiddenTargetsRecord = (state, conversationId = null, nextTargets = null) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    return state.hiddenTargetsByConversation || {};
  }
  return {
    ...(state.hiddenTargetsByConversation || {}),
    [normalizedConversationId]: Array.isArray(nextTargets) ? nextTargets : [],
  };
};

const reduceTimelineSuccess = (state, action) => {
  const conversationId = action.conversationId ?? state.activeConversationId;
  const hiddenTargets = getHiddenTargetsRecord(state, conversationId);
  const sourceItems = Array.isArray(action.items) ? action.items : [];
  const currentRecord = getTimelineRecord(state, conversationId);
  const nextGroupId = action.groupId ?? null;
  const existingItems =
    action.replaceTimeline !== true &&
    currentRecord.groupId === nextGroupId &&
    Array.isArray(currentRecord.items)
      ? currentRecord.items
      : [];
  const nextFrontier = resolveTimelineFrontier(currentRecord, action);
  const applicationFrontier = resolveTimelineNamedFrontier(currentRecord, action, {
    messageIdKey: 'applicationFrontierServerMsgId',
    serverSeqKey: 'applicationFrontierServerSeq',
  });
  const remoteDeliveredFrontier = resolveTimelineNamedFrontier(currentRecord, action, {
    messageIdKey: 'remoteDeliveredServerMsgId',
    serverSeqKey: 'remoteDeliveredServerSeq',
  });
  const remoteDecryptedFrontier = resolveTimelineNamedFrontier(currentRecord, action, {
    messageIdKey: 'remoteDecryptedServerMsgId',
    serverSeqKey: 'remoteDecryptedServerSeq',
  });
  const nextItems = applyTimelineDeletes(
    mergeTimelineItems(sourceItems, existingItems, {
      preferIncoming: nextFrontier.incomingIsCurrent,
    }),
    action.removedTargets,
  );
  const visibleItems = applyTimelineDeletes(nextItems, hiddenTargets);
  const nextRecord = {
    phase: 'ready',
    items: visibleItems,
    latestActivity: nextFrontier.incomingIsCurrent
      ? action.latestActivity ?? (action.replaceTimeline !== true && currentRecord.groupId === nextGroupId
        ? currentRecord.latestActivity ?? null : null)
      : currentRecord.latestActivity ?? null,
    groupId: nextGroupId,
    frontierServerMsgId: nextFrontier.serverMsgId,
    frontierServerSeq: nextFrontier.serverSeq,
    applicationFrontierServerMsgId: applicationFrontier.serverMsgId,
    applicationFrontierServerSeq: applicationFrontier.serverSeq,
    remoteDeliveredServerMsgId: remoteDeliveredFrontier.serverMsgId,
    remoteDeliveredServerSeq: remoteDeliveredFrontier.serverSeq,
    remoteDecryptedServerMsgId: remoteDecryptedFrontier.serverMsgId,
    remoteDecryptedServerSeq: remoteDecryptedFrontier.serverSeq,
    unresolvedServerSeqs: Array.isArray(action.unresolvedServerSeqs)
      ? action.unresolvedServerSeqs
      : action.replaceTimeline === true
        ? []
        : currentRecord.unresolvedServerSeqs,
    historyPhase: 'idle',
    historyBeforeSeq:
      toNullableServerSeq(action.historyBeforeSeq ?? null) ??
      resolveOldestTimelineServerSeq(visibleItems),
    historyHasMore:
      currentRecord.cleared === true
        ? false
        : action.historyHasMore != null
          ? action.historyHasMore === true
          : currentRecord.historyHasMore !== false,
    historyError: null,
    cleared: visibleItems.length === 0 ? currentRecord.cleared === true : false,
    stale: action.stale === true,
    error: null,
  };

  return {
    ...state,
    timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
    conversations: normalizeConversationsUnread(
      patchConversationSummary(
        state.conversations,
        action.conversationId,
        resolveConversationSummaryItem(visibleItems),
      ),
    ),
  };
};

export const createInitialMessengerV2State = ({ initialConversationId = null } = {}) => {
  const normalizedInitialConversationId = toStringOrNull(initialConversationId);
  return enforceThreadMetadataBoundary({
    bootstrap: {
      phase: 'idle',
      identityId: null,
      clientId: null,
      ciphersuite: null,
      keyPackageStatus: null,
      keyPackageRef: null,
      error: null,
    },
    contacts: {
      phase: 'idle',
      items: [],
      error: null,
    },
    conversationCatalog: {
      phase: 'idle',
      error: null,
    },
    composer: {
      phase: 'idle',
      error: null,
    },
    lifecycle: {
      hydrationReady: false,
    },
    call: createInitialMessengerV2CallState(),
    ongoingGroupCalls: [],
    threadsByConversation: normalizedInitialConversationId
      ? {
          [normalizedInitialConversationId]: createThreadState('idle'),
        }
      : {},
    timelinesByConversation: normalizedInitialConversationId
      ? {
          [normalizedInitialConversationId]: createTimelineState('idle'),
        }
      : {},
    hiddenConversationIds: [],
    hiddenTargetsByConversation: {},
    outboxByClientMsgId: {},
    conversations: [],
    activeConversationId: normalizedInitialConversationId,
    view: createInitialMessengerV2ViewState(),
  });
};

const reduceMessengerV2State = (state, action) => {
  switch (action?.type) {
    case 'lifecycle:hydration-ready-changed':
      if (state.lifecycle?.hydrationReady === (action.value === true)) {
        return state;
      }
      return {
        ...state,
        lifecycle: {
          ...state.lifecycle,
          hydrationReady: action.value === true,
        },
      };
    case 'calls:recovered':
      return { ...state, ongoingGroupCalls: action.calls };
    case 'call:state-updated':
      return {
        ...state,
        call:
          typeof action.value === 'function'
            ? action.value(state.call ?? createInitialMessengerV2CallState())
            : action.value,
      };
    case 'runtime:reset':
      return createInitialMessengerV2State({
        initialConversationId: action.conversationId ?? null,
      });
    case 'runtime:restore': {
      const restored = action.payload && typeof action.payload === 'object' ? action.payload : {};
      const restoredConversations = Array.isArray(restored.conversations)
        ? restored.conversations
        : state.conversations;
      const restoredThreadsByConversation = {};
      const restoredTimelinesByConversation =
        restored.timelinesByConversation && typeof restored.timelinesByConversation === 'object'
          ? restored.timelinesByConversation
          : {};
      const restoredHiddenTargetsByConversation =
        restored.hiddenTargetsByConversation &&
        typeof restored.hiddenTargetsByConversation === 'object'
          ? restored.hiddenTargetsByConversation
          : {};
      const restoredHiddenConversationIds = normalizeConversationIdList(
        restored.hiddenConversationIds,
      );
      const restoredOutboxByClientMsgId =
        restored.outboxByClientMsgId && typeof restored.outboxByClientMsgId === 'object'
          ? Object.fromEntries(
              Object.entries(restored.outboxByClientMsgId)
                .filter(([, entry]) => entry && typeof entry === 'object')
                .map(([clientMsgId, entry]) => [
                  clientMsgId,
                  {
                    ...entry,
                    clientMsgId,
                    phase:
                      entry.phase === 'failed'
                        ? 'failed'
                        : entry.phase === 'paused'
                          ? 'paused'
                          : 'deferred',
                    retryAt:
                      entry.phase === 'failed' || entry.phase === 'paused'
                        ? null
                        : entry.phase === 'deferred' && entry.retryAt == null
                          ? null
                          : Number(entry.retryAt) || 0,
                  },
                ]),
            )
          : {};
      const requestedRestoredActiveConversationId =
        action.routeConversationId != null
          ? toStringOrNull(action.routeConversationId)
          : restored.activeConversationId != null
            ? toStringOrNull(restored.activeConversationId)
            : toStringOrNull(state.activeConversationId);
      const restoredActiveConversationId = restoredHiddenConversationIds.includes(
        requestedRestoredActiveConversationId,
      )
        ? null
        : requestedRestoredActiveConversationId;
      const restoredBaseState = {
        ...state,
        hiddenConversationIds: restoredHiddenConversationIds,
      };
      const restoredVisibleConversations = filterHiddenConversations(
        restoredBaseState,
        markConversationsCatalogTrusted(normalizeConversationsUnread(restoredConversations), false),
      );
      const currentCatalogConversations = filterHiddenConversations(
        restoredBaseState,
        (Array.isArray(state.conversations) ? state.conversations : []).filter(
          isCatalogTrustedConversation,
        ),
      );
      const nextRestoredConversations = currentCatalogConversations.length
        ? replaceConversations(restoredVisibleConversations, currentCatalogConversations)
        : restoredVisibleConversations;

      return reconcileActiveThreadWithCanonicalConversation({
        ...restoredBaseState,
        conversationCatalog: {
          phase: 'loading',
          error: null,
        },
        contacts: restored.contacts
          ? {
              ...state.contacts,
              ...restored.contacts,
              error: null,
            }
          : state.contacts,
        conversations: seedConversation(nextRestoredConversations, restoredActiveConversationId),
        activeConversationId: restoredActiveConversationId,
        threadsByConversation: restoredActiveConversationId
          ? {
              ...restoredThreadsByConversation,
              [restoredActiveConversationId]: rehydrateRestoredThreadRecord(
                restoredThreadsByConversation?.[restoredActiveConversationId] ?? null,
              ),
            }
          : restoredThreadsByConversation,
        timelinesByConversation: restoredTimelinesByConversation,
        hiddenTargetsByConversation: restoredHiddenTargetsByConversation,
        outboxByClientMsgId: restoredOutboxByClientMsgId,
        view: {
          ...state.view,
          emojiPicker: {
            ...state.view?.emojiPicker,
            skinTone:
              typeof restored.emojiSkinTone === 'string'
                ? restored.emojiSkinTone
                : state.view?.emojiPicker?.skinTone || 'default',
          },
          emojiUsage:
            restored.emojiUsage && typeof restored.emojiUsage === 'object'
              ? restored.emojiUsage
              : state.view?.emojiUsage || {},
        },
      });
    }
    case 'outbox:enqueue': {
      const clientMsgId = toStringOrNull(action.entry?.clientMsgId ?? null);
      if (!clientMsgId) return state;
      const requestedPhase = toStringOrNull(action.entry?.phase ?? null);
      const phase = ['uploading', 'paused', 'sending', 'deferred', 'failed'].includes(
        requestedPhase,
      )
        ? requestedPhase
        : 'sending';
      return {
        ...state,
        outboxByClientMsgId: {
          ...(state.outboxByClientMsgId || {}),
          [clientMsgId]: {
            ...(state.outboxByClientMsgId?.[clientMsgId] || {}),
            ...action.entry,
            clientMsgId,
            phase,
          },
        },
      };
    }
    case 'outbox:defer': {
      const clientMsgId = toStringOrNull(action.clientMsgId ?? null);
      const current = clientMsgId ? state.outboxByClientMsgId?.[clientMsgId] : null;
      if (!clientMsgId || !current) return state;
      return {
        ...state,
        outboxByClientMsgId: {
          ...(state.outboxByClientMsgId || {}),
          [clientMsgId]: {
            ...current,
            phase: 'deferred',
            attempts: Math.max(0, Number(current.attempts) || 0) + 1,
            retryAt: action.retryAt == null ? null : action.retryAt,
            retryable: true,
            waitForOnline: action.waitForOnline === true,
            lastErrorCode: toStringOrNull(action.error?.code ?? null),
          },
        },
      };
    }
    case 'outbox:fail': {
      const clientMsgId = toStringOrNull(action.clientMsgId ?? null);
      const current = clientMsgId ? state.outboxByClientMsgId?.[clientMsgId] : null;
      if (!clientMsgId || !current) return state;
      return {
        ...state,
        outboxByClientMsgId: {
          ...(state.outboxByClientMsgId || {}),
          [clientMsgId]: {
            ...current,
            phase: 'failed',
            attempts: Math.max(0, Number(current.attempts) || 0) + 1,
            retryAt: null,
            retryable: action.retryable !== false,
            waitForOnline: false,
            lastErrorCode: toStringOrNull(action.error?.code ?? null),
          },
        },
      };
    }
    case 'outbox:resumeDeferred': {
      let changed = false;
      const retryAt = action.retryAt ?? Date.now();
      const nextOutbox = Object.fromEntries(
        Object.entries(state.outboxByClientMsgId || {}).map(([clientMsgId, entry]) => {
          if (entry?.phase !== 'deferred' || entry?.waitForOnline !== true) {
            return [clientMsgId, entry];
          }
          changed = true;
          return [
            clientMsgId,
            {
              ...entry,
              retryAt,
              waitForOnline: false,
            },
          ];
        }),
      );
      return changed ? { ...state, outboxByClientMsgId: nextOutbox } : state;
    }
    case 'outbox:remove': {
      const clientMsgId = toStringOrNull(action.clientMsgId ?? null);
      if (!clientMsgId || !state.outboxByClientMsgId?.[clientMsgId]) return state;
      const nextOutbox = { ...(state.outboxByClientMsgId || {}) };
      delete nextOutbox[clientMsgId];
      return { ...state, outboxByClientMsgId: nextOutbox };
    }
    case 'bootstrap:start':
      return {
        ...state,
        bootstrap: {
          ...state.bootstrap,
          phase: 'running',
          error: null,
        },
      };
    case 'bootstrap:success': {
      const nextBootstrap = {
        phase: 'ready',
        identityId: action.identityId ?? null,
        clientId: action.clientId ?? null,
        ciphersuite: action.ciphersuite ?? null,
        keyPackageStatus: action.keyPackageStatus ?? null,
        keyPackageRef: action.keyPackageRef ?? null,
        error: null,
      };
      if (
        state.bootstrap.phase === nextBootstrap.phase &&
        state.bootstrap.identityId === nextBootstrap.identityId &&
        state.bootstrap.clientId === nextBootstrap.clientId &&
        state.bootstrap.ciphersuite === nextBootstrap.ciphersuite &&
        state.bootstrap.keyPackageStatus === nextBootstrap.keyPackageStatus &&
        state.bootstrap.keyPackageRef === nextBootstrap.keyPackageRef &&
        state.bootstrap.error == null
      ) {
        return state;
      }
      return {
        ...state,
        bootstrap: nextBootstrap,
      };
    }
    case 'bootstrap:canonicalize': {
      const nextIdentityId =
        toStringOrNull(action.identityId ?? null) ??
        toStringOrNull(state.bootstrap.identityId ?? null) ??
        null;
      const nextClientId =
        toStringOrNull(action.clientId ?? null) ??
        toStringOrNull(state.bootstrap.clientId ?? null) ??
        null;
      const nextCiphersuite = action.ciphersuite ?? state.bootstrap.ciphersuite ?? null;
      if (
        state.bootstrap.phase === 'ready' &&
        state.bootstrap.identityId === nextIdentityId &&
        state.bootstrap.clientId === nextClientId &&
        state.bootstrap.ciphersuite === nextCiphersuite &&
        state.bootstrap.error == null
      ) {
        return state;
      }
      return {
        ...state,
        bootstrap: {
          ...state.bootstrap,
          phase: 'ready',
          identityId: nextIdentityId,
          clientId: nextClientId,
          ciphersuite: nextCiphersuite,
          error: null,
        },
      };
    }
    case 'bootstrap:error':
      return {
        ...state,
        bootstrap: {
          ...state.bootstrap,
          phase: 'error',
          error: action.error ?? null,
        },
      };
    case 'contacts:start':
      return {
        ...state,
        contacts: {
          ...state.contacts,
          phase: 'loading',
          error: null,
        },
      };
    case 'contacts:success':
      return {
        ...state,
        contacts: {
          phase: 'ready',
          items: Array.isArray(action.items) ? action.items : [],
          error: null,
        },
      };
    case 'contacts:error':
      return {
        ...state,
        contacts: {
          ...state.contacts,
          phase: 'error',
          error: action.error ?? null,
        },
      };
    case 'conversations:start':
      return {
        ...state,
        conversationCatalog: {
          phase: 'loading',
          error: null,
        },
      };
    case 'conversations:replace': {
      const activeConversationId = toStringOrNull(state.activeConversationId);
      const activeConversation = findConversationById(
        state.conversations,
        activeConversationId,
      );
      const keepMissingIds =
        activeConversation?.kind === 'room' && activeConversationId
          ? [activeConversationId]
          : [];
      const incomingActiveConversationIds = normalizeConversationIdList(
        (Array.isArray(action.items) ? action.items : [])
          .filter((item) => conversationScopesCurrentUserAsVisible(item))
          .map((item) => item?.id),
      );
      const nextHiddenConversationIds = removeHiddenConversationIds(
        state,
        incomingActiveConversationIds,
      );
      const nextState = {
        ...state,
        conversationCatalog: {
          phase: 'ready',
          error: null,
        },
        hiddenConversationIds: nextHiddenConversationIds,
      };
      const nextConversations = normalizeConversationsUnread(
        filterHiddenConversations(
          nextState,
          replaceConversations(
            state.conversations,
            markConversationsCatalogTrusted(action.items, true),
            { keepMissingIds },
          ),
        ),
      );
      const activeConversationStillListed =
        activeConversationId &&
        nextConversations.some(
          (conversation) => toStringOrNull(conversation?.id ?? null) === activeConversationId,
        );
      const activeConversationWasGroup = isGroupConversationRecord(
        findConversationById(state.conversations, activeConversationId),
      );
      if (activeConversationId && activeConversationWasGroup && !activeConversationStillListed) {
        const nextTimelinesByConversation = { ...(state.timelinesByConversation || {}) };
        const nextThreadsByConversation = { ...(state.threadsByConversation || {}) };
        delete nextTimelinesByConversation[activeConversationId];
        delete nextThreadsByConversation[activeConversationId];
        return reconcileActiveThreadWithCanonicalConversation({
          ...nextState,
          conversations: nextConversations,
          activeConversationId: null,
          activeThread: createThreadState('empty'),
          timelinesByConversation: nextTimelinesByConversation,
          threadsByConversation: nextThreadsByConversation,
        });
      }
      return reconcileActiveThreadWithCanonicalConversation({
        ...nextState,
        conversations: nextConversations,
      });
    }
    case 'conversations:error':
      return {
        ...state,
        conversationCatalog: {
          phase: 'error',
          error: action.error ?? null,
        },
      };
    case 'conversation:start':
      return {
        ...state,
        composer: {
          phase: 'creating',
          error: null,
        },
      };
    case 'conversation:created': {
      const normalizedConversationId =
        action.conversation?.id != null
          ? String(action.conversation.id)
          : state.activeConversationId;
      const nextState = {
        ...state,
        hiddenConversationIds: removeHiddenConversationIds(state, [normalizedConversationId]),
        conversations: normalizeConversationsUnread(
          upsertMergedConversation(
            state.conversations,
            markConversationCatalogTrusted(action.conversation, true),
          ),
        ),
      };
      return reconcileActiveThreadWithCanonicalConversation({
        ...nextState,
        composer: {
          phase: 'created',
          error: null,
        },
        activeConversationId: normalizedConversationId,
      });
    }
    case 'conversation:ready':
      return reconcileActiveThreadWithCanonicalConversation({
        ...state,
        hiddenConversationIds: removeHiddenConversationIds(state, [action.conversation?.id]),
        composer: {
          phase: 'ready',
          error: null,
        },
        conversations: normalizeConversationsUnread(
          upsertMergedConversation(
            state.conversations,
            markConversationCatalogTrusted(action.conversation, true),
          ),
        ),
      });
    case 'conversation:error':
      return {
        ...state,
        composer: {
          phase: 'error',
          error: action.error ?? null,
        },
      };
    case 'conversation:select': {
      const normalizedConversationId = toStringOrNull(action.conversationId ?? null);
      const previousConversationId = toStringOrNull(state.activeConversationId ?? null);
      const indexedState =
        previousConversationId && previousConversationId !== normalizedConversationId
          ? {
              ...state,
              threadsByConversation: updateThreadRecord(
                state,
                previousConversationId,
                state.activeThread,
              ),
              timelinesByConversation: updateTimelineRecord(
                state,
                previousConversationId,
                state.timeline,
              ),
            }
          : state;

      return reconcileActiveThreadWithCanonicalConversation({
        ...indexedState,
        activeConversationId: normalizedConversationId,
        conversations: normalizeConversationsUnread(indexedState.conversations),
      });
    }
    case 'conversation:typing': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const normalizedConversationId = toStringOrNull(conversationId);
      if (!normalizedConversationId) {
        return state;
      }
      const normalizedTypingUserId = action.typing === true ? toStringOrNull(action.userId) : null;

      const currentConversation = findConversationById(
        state.conversations,
        normalizedConversationId,
      );
      const currentTypingUserId = toStringOrNull(
        currentConversation?.typingUserId ?? currentConversation?.typing_user_id ?? null,
      );
      if (
        action.typing !== true &&
        action.userId != null &&
        currentTypingUserId &&
        currentTypingUserId !== toStringOrNull(action.userId)
      ) {
        return state;
      }
      const nextState = {
        ...state,
        conversations: patchConversationTyping(
          state.conversations,
          normalizedConversationId,
          action.typing === true,
          normalizedTypingUserId,
          action.activity,
        ),
      };

      return nextState;
    }
    case 'conversation:receipt': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const normalizedConversationId = toStringOrNull(conversationId);
      const normalizedUserId = toStringOrNull(action.userId ?? null);
      if (!normalizedConversationId || !normalizedUserId) {
        return state;
      }

      const receiptPatch = {
        userId: normalizedUserId,
        lastDeliveredAt: toStringOrNull(action.lastDeliveredAt ?? action.last_delivered_at ?? null),
        lastReadAt: toStringOrNull(action.lastReadAt ?? action.last_read_at ?? null),
        lastDeliveredMessageId: toStringOrNull(
          action.lastDeliveredMessageId ?? action.last_delivered_message_id ?? null,
        ),
        lastReadMessageId: toStringOrNull(
          action.lastReadMessageId ?? action.last_read_message_id ?? null,
        ),
        lastDeliveredServerMsgId: toStringOrNull(
          action.lastDeliveredServerMsgId ??
            action.last_delivered_server_msg_id ??
            action.lastDeliveredMessageId ??
            action.last_delivered_message_id ??
            null,
        ),
        lastDecryptedServerMsgId: toStringOrNull(
          action.lastDecryptedServerMsgId ?? action.last_decrypted_server_msg_id ?? null,
        ),
        lastReadServerMsgId: toStringOrNull(
          action.lastReadServerMsgId ??
            action.last_read_server_msg_id ??
            action.lastReadMessageId ??
            action.last_read_message_id ??
            null,
        ),
        lastDeliveredServerSeq: toNullableServerSeq(
          action.lastDeliveredServerSeq ?? action.last_delivered_server_seq ?? null,
        ),
        lastDecryptedServerSeq: toNullableServerSeq(
          action.lastDecryptedServerSeq ?? action.last_decrypted_server_seq ?? null,
        ),
        lastReadServerSeq: toNullableServerSeq(
          action.lastReadServerSeq ?? action.last_read_server_seq ?? null,
        ),
      };
      const nextState = {
        ...state,
        conversations: normalizeConversationsUnread(
          patchConversationParticipantsReceipt(
            state.conversations,
            normalizedConversationId,
            normalizedUserId,
            receiptPatch.lastDeliveredAt,
            receiptPatch.lastReadAt,
            receiptPatch.lastDeliveredMessageId,
            receiptPatch.lastReadMessageId,
            receiptPatch.lastDeliveredServerMsgId,
            receiptPatch.lastDecryptedServerMsgId,
            receiptPatch.lastReadServerMsgId,
            receiptPatch.lastDeliveredServerSeq,
            receiptPatch.lastDecryptedServerSeq,
            receiptPatch.lastReadServerSeq,
          ).map((conversation) => {
            if (
              String(conversation?.id ?? '') === normalizedConversationId &&
              (action.clearUnread === true || action.unreadCount != null)
            ) {
              return {
                ...conversation,
                current_user_last_read_server_msg_id:
                  receiptPatch.lastReadServerMsgId ??
                  resolveCurrentUserReadFrontierMsgId(conversation),
                current_user_last_read_server_seq:
                  receiptPatch.lastReadServerSeq ?? resolveCurrentUserReadFrontierSeq(conversation),
                unread_count:
                  action.unreadCount != null
                    ? toUnreadCount(action.unreadCount)
                    : action.clearUnread === true
                      ? 0
                      : toUnreadCount(conversation?.unread_count ?? 0),
              };
            }
            return conversation;
          }),
        ),
      };

      return nextState;
    }
    case 'conversation:presence': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const normalizedConversationId = toStringOrNull(conversationId);
      if (!normalizedConversationId || !action.presence || typeof action.presence !== 'object') {
        return state;
      }

      const nextState = {
        ...state,
        conversations: patchConversationPresence(
          state.conversations,
          normalizedConversationId,
          action.presence,
        ),
      };

      return nextState;
    }
    case 'conversation:metadata': {
      const conversation = action.conversation;
      const normalizedConversationId = toStringOrNull(conversation?.id ?? null);
      if (!normalizedConversationId || !conversation || typeof conversation !== 'object') {
        return state;
      }
      if (
        resolveScopedConversationDeletedAt(conversation) &&
        !conversationScopesCurrentUserAsVisible(conversation)
      ) {
        return reduceMessengerV2State(state, {
          type: 'conversation:hide',
          conversationId: normalizedConversationId,
        });
      }
      if (
        isConversationHidden(state, normalizedConversationId) &&
        !conversationScopesCurrentUserAsVisible(conversation)
      ) {
        return state;
      }

      const listConversation = findConversationById(state.conversations, normalizedConversationId);
      const mergedConversation = normalizeConversationUnread(
        resolveCanonicalConversation(
          state,
          normalizedConversationId,
          markConversationCatalogTrusted(conversation, true),
        ),
      );
      const nextConversations = normalizeConversationsUnread(
        upsertCanonicalConversation(state.conversations, mergedConversation),
      );

      const currentTimeline = getTimelineRecord(state, normalizedConversationId);
      const nextTimelineRecord =
        String(state.activeConversationId ?? '') !== normalizedConversationId &&
        (Number(conversation?.unread_count ?? 0) > 0 ||
          (toStringOrNull(conversation?.last_server_msg_id ?? null) &&
            toStringOrNull(listConversation?.last_server_msg_id ?? null) !==
              toStringOrNull(conversation?.last_server_msg_id ?? null)))
          ? {
              ...currentTimeline,
              stale: true,
            }
          : currentTimeline;

      const nextState = {
        ...state,
        hiddenConversationIds: conversationScopesCurrentUserAsVisible(mergedConversation)
          ? removeHiddenConversationIds(state, [normalizedConversationId])
          : state.hiddenConversationIds,
        conversations: nextConversations,
        timelinesByConversation: updateTimelineRecord(
          state,
          normalizedConversationId,
          nextTimelineRecord,
        ),
      };

      return nextState;
    }
    case 'conversation:hide': {
      const conversationId = toStringOrNull(action.conversationId ?? null);
      if (!conversationId) return state;
      const nextTimelinesByConversation = { ...(state.timelinesByConversation || {}) };
      const nextThreadsByConversation = { ...(state.threadsByConversation || {}) };
      delete nextTimelinesByConversation[conversationId];
      delete nextThreadsByConversation[conversationId];
      return {
        ...state,
        hiddenConversationIds: addHiddenConversationId(state, conversationId),
        conversations: (Array.isArray(state.conversations) ? state.conversations : []).filter(
          (conversation) => toStringOrNull(conversation?.id ?? null) !== conversationId,
        ),
        activeConversationId:
          toStringOrNull(state.activeConversationId ?? null) === conversationId
            ? null
            : state.activeConversationId,
        activeThread:
          toStringOrNull(state.activeConversationId ?? null) === conversationId
            ? createThreadState('empty')
            : state.activeThread,
        timelinesByConversation: nextTimelinesByConversation,
        threadsByConversation: nextThreadsByConversation,
      };
    }
    case 'composer:sendStart':
      return {
        ...state,
        composer: {
          phase: 'sending',
          error: null,
        },
      };
    case 'composer:sendSuccess':
      return {
        ...state,
        composer: {
          phase: 'ready',
          error: null,
        },
      };
    case 'composer:sendError':
      return {
        ...state,
        composer: {
          phase: 'error',
          error: action.error ?? null,
        },
      };
    case 'thread:start':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getThreadRecord(state, conversationId);
        const hasCachedThread = Boolean(
          currentRecord?.snapshot ||
            currentRecord?.readyInfo ||
            currentRecord?.phase === 'ready' ||
            currentRecord?.phase === 'pending',
        );
        const nextRecord = {
          ...currentRecord,
          phase: hasCachedThread ? currentRecord.phase : 'loading',
          error: null,
          stale: action.invalidate === true ? true : currentRecord.stale,
          syncing: true,
        };
        return {
          ...state,
          threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getThreadRecord(state, conversationId);
        const hasCachedThread = Boolean(
          currentRecord?.snapshot ||
            currentRecord?.readyInfo ||
            currentRecord?.phase === 'ready' ||
            currentRecord?.phase === 'pending',
        );
        const nextRecord = {
          ...currentRecord,
          phase: hasCachedThread ? currentRecord.phase : 'loading',
          error: null,
          stale: action.invalidate === true ? true : currentRecord.stale,
          syncing: true,
        };
        return {
          ...state,
          threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
        };
      }
    case 'thread:seeded':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        if (
          isConversationHidden(state, conversationId) &&
          !conversationScopesCurrentUserAsVisible(action.conversation)
        ) {
          return state;
        }
        const mergedConversation = resolveCanonicalConversation(
          state,
          conversationId,
          action.conversation ?? null,
        );
        const nextRecord = syncMembershipReadyInfoWithCanonicalConversation(
          {
            phase: 'pending',
            conversation: null,
            snapshot: action.snapshot ?? null,
            readyInfo: normalizeReadyInfo(action.readyInfo, false),
            stale: false,
            syncing: true,
            error: null,
          },
          mergedConversation,
        );
        return {
          ...state,
          hiddenConversationIds: conversationScopesCurrentUserAsVisible(mergedConversation)
            ? removeHiddenConversationIds(state, [conversationId])
            : state.hiddenConversationIds,
          threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
          conversations: normalizeConversationsUnread(
            upsertCanonicalConversation(state.conversations, mergedConversation),
          ),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        if (
          isConversationHidden(state, conversationId) &&
          !conversationScopesCurrentUserAsVisible(action.conversation)
        ) {
          return state;
        }
        const mergedConversation = resolveCanonicalConversation(
          state,
          conversationId,
          action.conversation ?? null,
        );
        const nextRecord = syncMembershipReadyInfoWithCanonicalConversation(
          {
            phase: 'pending',
            conversation: null,
            snapshot: action.snapshot ?? null,
            readyInfo: normalizeReadyInfo(action.readyInfo, false),
            stale: false,
            syncing: true,
            error: null,
          },
          mergedConversation,
        );
        return {
          ...state,
          hiddenConversationIds: conversationScopesCurrentUserAsVisible(mergedConversation)
            ? removeHiddenConversationIds(state, [conversationId])
            : state.hiddenConversationIds,
          activeConversationId: String(conversationId),
          threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
          conversations: normalizeConversationsUnread(
            upsertCanonicalConversation(state.conversations, mergedConversation),
          ),
        };
      }
    case 'thread:success':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        if (
          isConversationHidden(state, conversationId) &&
          !conversationScopesCurrentUserAsVisible(action.conversation)
        ) {
          return state;
        }
        const mergedConversation = resolveCanonicalConversation(
          state,
          conversationId,
          action.conversation ?? null,
        );
        const nextRecord = syncMembershipReadyInfoWithCanonicalConversation(
          {
            phase: action.ready ? 'ready' : 'pending',
            conversation: null,
            snapshot: action.snapshot ?? null,
            readyInfo: normalizeReadyInfo(action.readyInfo, action.ready === true),
            stale: false,
            syncing: false,
            error: null,
          },
          mergedConversation,
        );
        return {
          ...state,
          hiddenConversationIds: conversationScopesCurrentUserAsVisible(mergedConversation)
            ? removeHiddenConversationIds(state, [conversationId])
            : state.hiddenConversationIds,
          threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
          conversations: normalizeConversationsUnread(
            upsertCanonicalConversation(state.conversations, mergedConversation),
          ),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        if (
          isConversationHidden(state, conversationId) &&
          !conversationScopesCurrentUserAsVisible(action.conversation)
        ) {
          return state;
        }
        const mergedConversation = resolveCanonicalConversation(
          state,
          conversationId,
          action.conversation ?? null,
        );
        const nextRecord = syncMembershipReadyInfoWithCanonicalConversation(
          {
            phase: action.ready ? 'ready' : 'pending',
            conversation: null,
            snapshot: action.snapshot ?? null,
            readyInfo: normalizeReadyInfo(action.readyInfo, action.ready === true),
            stale: false,
            syncing: false,
            error: null,
          },
          mergedConversation,
        );
        return {
          ...state,
          hiddenConversationIds: conversationScopesCurrentUserAsVisible(mergedConversation)
            ? removeHiddenConversationIds(state, [conversationId])
            : state.hiddenConversationIds,
          activeConversationId: String(conversationId),
          threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
          conversations: normalizeConversationsUnread(
            upsertCanonicalConversation(state.conversations, mergedConversation),
          ),
        };
      }
    case 'thread:error':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getThreadRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          phase: 'error',
          syncing: false,
          error: action.error ?? null,
        };
        return {
          ...state,
          threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getThreadRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          phase: 'error',
          syncing: false,
          error: action.error ?? null,
        };
        return {
          ...state,
          threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
        };
      }
    case 'thread:refreshError': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getThreadRecord(state, conversationId);
      const nextRecord = {
        ...currentRecord,
        syncing: false,
        error: action.error ?? null,
      };
      return {
        ...state,
        threadsByConversation: updateThreadRecord(state, conversationId, nextRecord),
      };
    }
    case 'group:member_added': {
      const normalizedConversationId = toStringOrNull(action.conversationId);
      if (!normalizedConversationId) {
        return state;
      }

      const incomingConversation =
        action.conversation && typeof action.conversation === 'object'
          ? markConversationCatalogTrusted(
              {
                ...action.conversation,
                id:
                  toStringOrNull(action.conversation.id ?? normalizedConversationId) ||
                  normalizedConversationId,
              },
              true,
            )
          : null;
      if (
        isConversationHidden(state, normalizedConversationId) &&
        !conversationScopesCurrentUserAsActive(incomingConversation)
      ) {
        return state;
      }
      const canonicalConversation = resolveCanonicalConversation(
        state,
        normalizedConversationId,
        incomingConversation,
      );
      const nextConversations = canonicalConversation
        ? upsertCanonicalConversation(state.conversations, canonicalConversation)
        : state.conversations;
      const currentThread = getThreadRecord(state, normalizedConversationId);
      const hasRuntimeRecord = Boolean(currentThread && canonicalConversation);
      const normalizedAddedUserId = toStringOrNull(action.addedUserId);
      const normalizedCurrentUserId = toStringOrNull(action.currentUserId);
      const addedCurrentUser =
        normalizedCurrentUserId && normalizedCurrentUserId === normalizedAddedUserId;
      const nextThreadRecord = hasRuntimeRecord
        ? addedCurrentUser
          ? createActiveMembershipThreadRecord(currentThread, canonicalConversation)
          : syncThreadConversationMetadata(currentThread, canonicalConversation)
        : currentThread;
      const nextState = {
        ...state,
        hiddenConversationIds: addedCurrentUser
          ? removeHiddenConversationIds(state, [normalizedConversationId])
          : state.hiddenConversationIds,
        conversations: normalizeConversationsUnread(nextConversations),
        threadsByConversation: hasRuntimeRecord
          ? updateThreadRecord(state, normalizedConversationId, nextThreadRecord)
          : state.threadsByConversation,
      };

      return nextState;
    }
    case 'group:member_removed': {
      const normalizedConversationId = toStringOrNull(action.conversationId);
      const normalizedRemovedUserId = toStringOrNull(action.removedUserId);
      const normalizedCurrentUserId = toStringOrNull(action.currentUserId);
      if (!normalizedConversationId || !normalizedRemovedUserId) {
        return state;
      }

      const incomingConversation =
        action.conversation && typeof action.conversation === 'object'
          ? markConversationCatalogTrusted(
              {
                ...action.conversation,
                id:
                  toStringOrNull(action.conversation.id ?? normalizedConversationId) ||
                  normalizedConversationId,
              },
              true,
            )
          : null;
      if (
        isConversationHidden(state, normalizedConversationId) &&
        !conversationScopesCurrentUserAsActive(incomingConversation)
      ) {
        return state;
      }
      const canonicalConversation = incomingConversation
        ? resolveCanonicalConversation(state, normalizedConversationId, incomingConversation)
        : removeConversationParticipantByUserId(
            resolveCanonicalConversation(state, normalizedConversationId, null),
            normalizedRemovedUserId,
          );
      const nextConversations = canonicalConversation
        ? upsertCanonicalConversation(state.conversations, canonicalConversation)
        : state.conversations;
      const currentThread = getThreadRecord(state, normalizedConversationId);
      const removedCurrentUser =
        normalizedCurrentUserId && normalizedCurrentUserId === normalizedRemovedUserId;
      const removalStillApplies =
        removedCurrentUser && !conversationScopesCurrentUserAsActive(canonicalConversation);
      const nextThreadRecord = currentThread
        ? removalStillApplies
          ? {
              ...currentThread,
              phase: 'ready',
              conversation: null,
              readyInfo: createFormerMemberReadyInfo(
                currentThread.readyInfo,
                canonicalConversation,
              ),
              stale: false,
              error: null,
            }
          : syncThreadConversationMetadata(currentThread, canonicalConversation)
        : currentThread;
      const nextState = {
        ...state,
        conversations: normalizeConversationsUnread(nextConversations),
        threadsByConversation: nextThreadRecord
          ? updateThreadRecord(state, normalizedConversationId, nextThreadRecord)
          : state.threadsByConversation,
      };

      return nextState;
    }
    case 'timeline:start': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const hasCachedItems = Array.isArray(currentRecord.items) && currentRecord.items.length > 0;
      const hasLocalClearBoundary = currentRecord.cleared === true;
      const nextRecord = {
        ...currentRecord,
        phase: hasCachedItems
          ? currentRecord.phase === 'empty'
            ? 'ready'
            : currentRecord.phase
          : hasLocalClearBoundary
            ? 'ready'
            : 'loading',
        stale: hasCachedItems ? true : currentRecord.stale,
        error: null,
      };
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
      };
    }
    case 'timeline:success':
      return reduceTimelineSuccess(state, action);
    case 'timeline:error': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const hasCachedItems = Array.isArray(currentRecord.items) && currentRecord.items.length > 0;
      const nextRecord = {
        ...currentRecord,
        phase: hasCachedItems
          ? currentRecord.phase === 'empty'
            ? 'ready'
            : currentRecord.phase
          : 'error',
        error: action.error ?? null,
      };
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
      };
    }
    case 'timeline:clearConversation': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const nextRecord = {
        phase: 'ready',
        items: [],
        groupId: action.groupId ?? getTimelineRecord(state, conversationId).groupId ?? null,
        frontierServerMsgId: null,
        frontierServerSeq: null,
        applicationFrontierServerMsgId: null,
        applicationFrontierServerSeq: null,
        remoteDeliveredServerMsgId: null,
        remoteDeliveredServerSeq: null,
        remoteDecryptedServerMsgId: null,
        remoteDecryptedServerSeq: null,
        unresolvedServerSeqs: [],
        historyPhase: 'idle',
        historyBeforeSeq: null,
        historyHasMore: false,
        historyError: null,
        cleared: true,
        stale: false,
        error: null,
      };
      const nextConversations = normalizeConversationsUnread(
        (Array.isArray(state.conversations) ? state.conversations : []).map((conversation) =>
          toStringOrNull(conversation?.id ?? null) === toStringOrNull(conversationId)
            ? {
                ...conversation,
                last_message_preview: null,
                last_message_at: null,
                last_server_msg_id: null,
                last_server_seq: null,
                subtitle: null,
                unread_count: 0,
              }
            : conversation,
        ),
      );
      if (!isActiveConversationAction(state, action)) {
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
          conversations: nextConversations,
        };
      }
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        conversations: nextConversations,
      };
    }
    case 'timeline:historyStart':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        if (currentRecord.cleared === true || currentRecord.historyHasMore === false) {
          const nextRecord = {
            ...currentRecord,
            historyPhase: 'idle',
            historyHasMore: false,
            historyError: null,
          };
          return {
            ...state,
            timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
          };
        }
        const nextRecord = {
          ...currentRecord,
          historyPhase: 'loading',
          historyError: null,
        };
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        if (currentRecord.cleared === true || currentRecord.historyHasMore === false) {
          const nextRecord = {
            ...currentRecord,
            historyPhase: 'idle',
            historyHasMore: false,
            historyError: null,
          };
          return {
            ...state,
            timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
          };
        }
        const nextRecord = {
          ...currentRecord,
          historyPhase: 'loading',
          historyError: null,
        };
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        };
      }
    case 'timeline:historySuccess':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const hiddenTargets = getHiddenTargetsRecord(state, conversationId);
        const sourceItems = Array.isArray(action.items) ? action.items : [];
        const nextItems = applyTimelineDeletes(
          currentRecord.cleared === true
            ? currentRecord.items
            : mergeTimelineItems(sourceItems, currentRecord.items, { preferIncoming: false }),
          action.removedTargets,
        );
        const visibleItems = applyTimelineDeletes(nextItems, hiddenTargets);
        const nextRecord = {
          ...currentRecord,
          phase: currentRecord.phase === 'empty' ? 'ready' : currentRecord.phase,
          items: visibleItems,
          groupId: action.groupId ?? currentRecord.groupId ?? null,
          historyPhase: 'idle',
          historyBeforeSeq:
            toNullableServerSeq(action.nextBeforeServerSeq ?? null) ??
            resolveOldestTimelineServerSeq(visibleItems),
          historyHasMore: currentRecord.cleared === true ? false : action.hasMore === true,
          historyError: null,
        };
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const hiddenTargets = getHiddenTargetsRecord(state, conversationId);
        const sourceItems = Array.isArray(action.items) ? action.items : [];
        const nextItems = applyTimelineDeletes(
          currentRecord.cleared === true
            ? currentRecord.items
            : mergeTimelineItems(sourceItems, currentRecord.items, { preferIncoming: false }),
          action.removedTargets,
        );
        const visibleItems = applyTimelineDeletes(nextItems, hiddenTargets);
        const nextRecord = {
          ...currentRecord,
          phase: currentRecord.phase === 'empty' ? 'ready' : currentRecord.phase,
          items: visibleItems,
          groupId: action.groupId ?? currentRecord.groupId ?? null,
          historyPhase: 'idle',
          historyBeforeSeq:
            toNullableServerSeq(action.nextBeforeServerSeq ?? null) ??
            resolveOldestTimelineServerSeq(visibleItems),
          historyHasMore: currentRecord.cleared === true ? false : action.hasMore === true,
          historyError: null,
        };
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        };
      }
    case 'timeline:historyError':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          historyPhase: 'error',
          historyError: action.error ?? null,
        };
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          historyPhase: 'error',
          historyError: action.error ?? null,
        };
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        };
      }
    case 'timeline:appendOptimistic':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          phase: currentRecord.phase === 'empty' ? 'ready' : currentRecord.phase,
          items: upsertTimelineItem(currentRecord.items, action.item),
          groupId: action.groupId ?? currentRecord.groupId,
          historyPhase:
            currentRecord.historyPhase === 'loading' ? 'idle' : currentRecord.historyPhase,
          historyError: null,
          error: null,
        };
        const summaryItem = resolveConversationSummaryItem(nextRecord.items);
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
          conversations: normalizeConversationsUnread(
            patchConversationSummary(state.conversations, action.conversationId, summaryItem),
          ),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          phase: currentRecord.phase === 'empty' ? 'ready' : currentRecord.phase,
          items: upsertTimelineItem(currentRecord.items, action.item),
          groupId: action.groupId ?? currentRecord.groupId,
          historyPhase:
            currentRecord.historyPhase === 'loading' ? 'idle' : currentRecord.historyPhase,
          historyError: null,
          error: null,
        };
        const summaryItem = resolveConversationSummaryItem(nextRecord.items);
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
          conversations: normalizeConversationsUnread(
            patchConversationSummary(state.conversations, action.conversationId, summaryItem),
          ),
        };
      }
    case 'timeline:ackMessage':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          phase: 'ready',
          items: upsertTimelineItem(currentRecord.items, action.item),
          groupId: action.groupId ?? currentRecord.groupId,
          historyPhase:
            currentRecord.historyPhase === 'loading' ? 'idle' : currentRecord.historyPhase,
          historyError: null,
          error: null,
        };
        const summaryItem = resolveConversationSummaryItem(nextRecord.items);
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
          conversations: normalizeConversationsUnread(
            patchConversationSummary(state.conversations, action.conversationId, summaryItem),
          ),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          phase: 'ready',
          items: upsertTimelineItem(currentRecord.items, action.item),
          groupId: action.groupId ?? currentRecord.groupId,
          historyPhase:
            currentRecord.historyPhase === 'loading' ? 'idle' : currentRecord.historyPhase,
          historyError: null,
          error: null,
        };
        const summaryItem = resolveConversationSummaryItem(nextRecord.items);
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
          conversations: normalizeConversationsUnread(
            patchConversationSummary(state.conversations, action.conversationId, summaryItem),
          ),
        };
      }
    case 'timeline:markFailed':
      if (!isActiveConversationAction(state, action)) {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          items: currentRecord.items.map((item) => {
            const itemClientMsgId = item?.clientMsgId != null ? String(item.clientMsgId) : null;
            const targetClientMsgId =
              action.clientMsgId != null ? String(action.clientMsgId) : null;
            if (!itemClientMsgId || !targetClientMsgId || itemClientMsgId !== targetClientMsgId) {
              return item;
            }
            return {
              ...item,
              failed: true,
              optimistic: true,
              localPendingUpload: false,
              state: 'failed',
              retryable: action.retryable !== false,
              failureCode: toStringOrNull(action.error?.code ?? null),
            };
          }),
          error: action.error ?? null,
        };
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        };
      }
      {
        const conversationId = action.conversationId ?? state.activeConversationId;
        const currentRecord = getTimelineRecord(state, conversationId);
        const nextRecord = {
          ...currentRecord,
          items: currentRecord.items.map((item) => {
            const itemClientMsgId = item?.clientMsgId != null ? String(item.clientMsgId) : null;
            const targetClientMsgId =
              action.clientMsgId != null ? String(action.clientMsgId) : null;
            if (!itemClientMsgId || !targetClientMsgId || itemClientMsgId !== targetClientMsgId) {
              return item;
            }
            return {
              ...item,
              failed: true,
              optimistic: true,
              localPendingUpload: false,
              state: 'failed',
              retryable: action.retryable !== false,
              failureCode: toStringOrNull(action.error?.code ?? null),
            };
          }),
          error: action.error ?? null,
        };
        return {
          ...state,
          timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        };
      }
    case 'timeline:updateUpload': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const targetClientMsgId = toStringOrNull(action.clientMsgId ?? null);
      if (!targetClientMsgId) return state;
      const progress = Math.max(0, Math.min(100, Math.round(Number(action.progress) || 0)));
      const uploadState = toStringOrNull(action.state ?? null) || 'uploading';
      const nextRecord = {
        ...currentRecord,
        items: currentRecord.items.map((item) =>
          toStringOrNull(item?.clientMsgId ?? null) === targetClientMsgId
            ? {
                ...item,
                failed: uploadState === 'failed',
                optimistic: true,
                localPendingUpload: ['preparing', 'uploading', 'paused'].includes(uploadState),
                state: uploadState,
                uploadProgress: progress,
                uploadError: action.error ? String(action.error?.message || action.error) : null,
              }
            : item,
        ),
        error: uploadState === 'failed' ? (action.error ?? null) : null,
      };
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
      };
    }
    case 'timeline:removeOptimistic': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const targetClientMsgId = toStringOrNull(action.clientMsgId ?? null);
      if (!targetClientMsgId) return state;
      const nextItems = currentRecord.items.filter(
        (item) =>
          !(
            item?.optimistic === true &&
            toStringOrNull(item?.clientMsgId ?? null) === targetClientMsgId
          ),
      );
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, {
          ...currentRecord,
          items: nextItems,
          error: null,
        }),
      };
    }
    case 'timeline:markQueued': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const targetClientMsgId = toStringOrNull(action.clientMsgId ?? null);
      const nextRecord = {
        ...currentRecord,
        items: currentRecord.items.map((item) =>
          toStringOrNull(item?.clientMsgId ?? null) === targetClientMsgId
            ? {
                ...item,
                failed: false,
                optimistic: true,
                localPendingUpload: false,
                state: 'queued',
                retryable: true,
                failureCode: null,
              }
            : item,
        ),
        error: null,
      };
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
      };
    }
    case 'timeline:markSending': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const targetClientMsgId = toStringOrNull(action.clientMsgId ?? null);
      const nextRecord = {
        ...currentRecord,
        items: currentRecord.items.map((item) =>
          toStringOrNull(item?.clientMsgId ?? null) === targetClientMsgId
            ? {
                ...item,
                attachments: Array.isArray(action.attachments)
                  ? action.attachments
                  : item.attachments,
                body: toStringOrNull(action.body ?? null) || item.body,
                rawText: toStringOrNull(action.rawText ?? null) || item.rawText,
                failed: false,
                optimistic: true,
                localPendingUpload: false,
                state: 'sending',
                retryable: true,
                failureCode: null,
              }
            : item,
        ),
        error: null,
      };
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
      };
    }
    case 'timeline:applyEdit': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const nextItems = applyTimelineEdit(
        currentRecord.items,
        action.target,
        action.body,
        action.editedAt,
        action.linkPreview ?? null,
      );
      const nextRecord = {
        ...currentRecord,
        items: nextItems,
      };
      const nextState = {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        conversations: patchConversationSummary(
          state.conversations,
          conversationId,
          resolveConversationSummaryItem(nextItems),
        ),
      };
      if (!isActiveConversationAction(state, action)) {
        return nextState;
      }
      return {
        ...nextState,
      };
    }
    case 'timeline:applyDelete': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const nextItems = applyTimelineDelete(currentRecord.items, action.target);
      const nextRecord = {
        ...currentRecord,
        items: nextItems,
      };
      const nextState = {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        conversations: patchConversationSummary(
          state.conversations,
          conversationId,
          resolveConversationSummaryItem(nextItems),
        ),
      };
      if (!isActiveConversationAction(state, action)) {
        return nextState;
      }
      return {
        ...nextState,
      };
    }
    case 'timeline:applyReaction': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const nextRecord = {
        ...currentRecord,
        items: applyMessengerV2TimelineReaction(currentRecord.items, {
          target: action.target,
          reaction: action.reaction,
          authorId: action.authorId,
          currentUserId: action.currentUserId,
          operationId: action.operationId,
        }),
      };
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
      };
    }
    case 'timeline:settleReaction': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const nextRecord = {
        ...currentRecord,
        items: settleMessengerV2TimelineReaction(currentRecord.items, {
          target: action.target,
          operationId: action.operationId,
          rollback: action.rollback === true,
        }),
      };
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
      };
    }
    case 'timeline:markReactionState': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const nextRecord = {
        ...currentRecord,
        items: markMessengerV2TimelineReactionState(currentRecord.items, {
          target: action.target,
          operationId: action.operationId,
          state: action.state,
          retryable: action.retryable,
          errorCode: action.error?.code ?? action.errorCode ?? null,
        }),
      };
      return {
        ...state,
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
      };
    }
    case 'timeline:deleteForMe': {
      const conversationId = action.conversationId ?? state.activeConversationId;
      const currentRecord = getTimelineRecord(state, conversationId);
      const currentHiddenTargets = getHiddenTargetsRecord(state, conversationId);
      const nextHiddenTargets = [...currentHiddenTargets, action.target].filter(Boolean);
      const nextItems = applyTimelineDelete(currentRecord.items, action.target);
      const nextRecord = {
        ...currentRecord,
        items: nextItems,
      };
      const nextState = {
        ...state,
        hiddenTargetsByConversation: updateHiddenTargetsRecord(
          state,
          conversationId,
          nextHiddenTargets,
        ),
        timelinesByConversation: updateTimelineRecord(state, conversationId, nextRecord),
        conversations: patchConversationSummary(
          state.conversations,
          conversationId,
          resolveConversationSummaryItem(nextItems),
        ),
      };
      if (!isActiveConversationAction(state, action)) {
        return nextState;
      }
      return {
        ...nextState,
      };
    }
    default:
      return state;
  }
};

export const messengerV2Reducer = (state, action) => {
  const currentView =
    state.view && typeof state.view === 'object' ? state.view : createInitialMessengerV2ViewState();
  const stateWithView = state.view === currentView ? state : { ...state, view: currentView };
  const nextView = reduceMessengerV2ViewState(currentView, action);
  if (nextView !== currentView) {
    return enforceThreadMetadataBoundary({
      ...stateWithView,
      view: nextView,
    });
  }
  return enforceThreadMetadataBoundary(reduceMessengerV2State(stateWithView, action));
};

export default messengerV2Reducer;
